import {
  AlertTriangle,
  ArrowRight,
  Building2,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  ClipboardCheck,
  Copy,
  Clock3,
  DollarSign,
  Droplets,
  Eye,
  Fan,
  FileText,
  Hammer,
  Home,
  House,
  ListChecks,
  Loader2,
  MapPin,
  Menu,
  MessageSquareText,
  PackageCheck,
  PlugZap,
  RefreshCw,
  Search,
  Share2,
  ShieldCheck,
  SlidersHorizontal,
  Sofa,
  Sparkles,
  Table2,
  Trees,
  Wrench,
  X,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { Children, useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { sampleAgentReport } from './data/sampleReport';
import {
  captureAnalyticsEvent,
  getReportShape,
  getSeverityMix,
  initializeAnalytics,
  setAnalyticsContext,
  type AnalyticsPage,
} from './lib/analytics';
import { balancedVisibility, defaultBuyerShareState, fullVisibility, minimalVisibility } from './lib/defaultState';
import { formatCostRange, formatCurrency, formatSystemLabel } from './lib/format';
import {
  getRecommendedNegotiationIssueNumbers,
  getIssuesSortedByPriority,
  getNegotiationTotals,
  getSeverityCounts,
  getSystemSummaries,
  issueCostAvg,
  issueCostMax,
  issueCostMin,
  issueIsSafety,
  normalizeSeverity,
  shapeBuyerPayload,
} from './lib/selectors';
import type {
  AgentReportResponse,
  BuyerReportResponse,
  BuyerShareState,
  BuyerVisibility,
  GeneratedDocument,
  InspectionIssue,
  InspectionReportPayload,
  SystemSummary,
  WorkspaceState,
} from './types/report';

type Page = 'overview' | 'systems' | 'issues' | 'negotiation' | 'client-prep' | 'buyer';
type BadgeTone = 'critical' | 'moderate' | 'minor' | 'success' | 'neutral';
type TimeframeBucketId = 'beforeClose' | 'nextMonth' | 'afterMoveIn' | 'later';
type HouseMapSide = 'left' | 'right';
type HouseMapTone = 'critical' | 'moderate' | 'minor' | 'neutral';
type IssueSeverity = 'all' | 'critical' | 'moderate' | 'minor';
type IssueSortMode = 'priority' | 'cost' | 'system' | 'timeframe';
type IssueViewMode = 'cards' | 'table';
type StateUpdate = {
  path: string;
  value: unknown;
};
type ReportJobCardProps = {
  icon: LucideIcon;
  tone: 'systems' | 'issues' | 'negotiation' | 'client';
  title: string;
  job: string;
  detail: string;
  metric: string;
  actionLabel: string;
  page: Exclude<Page, 'overview' | 'buyer'>;
};
type HouseMapAnchor = {
  x: number;
  y: number;
  lineEndX: number;
  lineEndY: number;
  side: HouseMapSide;
};
type HouseMapHotspot = {
  d: string;
  label: string;
};
type HouseMapConnectorLine = {
  id: string;
  tone: HouseMapTone;
  path: string;
  startX: number;
  startY: number;
};

const getAnalyticsPage = (page: Exclude<Page, 'buyer'>): AnalyticsPage => {
  if (page === 'negotiation') return 'binsr_draft';
  if (page === 'client-prep') return 'client_prep';
  return page;
};

const navItems: Array<{ page: Page; label: string; icon: typeof Home }> = [
  { page: 'overview', label: 'Overview', icon: Home },
  { page: 'systems', label: 'Systems', icon: ClipboardCheck },
  { page: 'issues', label: 'All Issues', icon: ListChecks },
  { page: 'negotiation', label: 'BINSR Draft', icon: Wrench },
  { page: 'client-prep', label: 'Client Prep', icon: MessageSquareText },
];

const loadInitialPage = (): Page => {
  const path = window.location.pathname;
  if (path.startsWith('/share/')) return 'buyer';
  if (path.includes('/systems')) return 'systems';
  if (path.includes('/issues')) return 'issues';
  if (path.includes('/negotiation')) return 'negotiation';
  if (path.includes('/client-prep')) return 'client-prep';
  return 'overview';
};

const getIssueSeverityFromSearch = (): IssueSeverity => {
  const severity = new URLSearchParams(window.location.search).get('severity');
  return severity === 'critical' || severity === 'moderate' || severity === 'minor' ? severity : 'all';
};

const getIssueNumberFromSearch = (): number | null => {
  const issueNumber = Number(new URLSearchParams(window.location.search).get('issue'));
  return Number.isInteger(issueNumber) && issueNumber > 0 ? issueNumber : null;
};

const updatePath = (page: Page, severity: IssueSeverity = 'all', issueNumber?: number) => {
  if (page === 'buyer') return;
  const token = window.location.pathname.match(/^\/a\/([^/]+)/)?.[1] || 'sample';
  const suffix = page === 'overview' ? '' : `/${page === 'client-prep' ? 'client-prep' : page}`;
  const searchParams = new URLSearchParams();
  if (page === 'issues' && severity !== 'all') searchParams.set('severity', severity);
  if (page === 'issues' && issueNumber) searchParams.set('issue', String(issueNumber));
  const query = searchParams.size ? `?${searchParams.toString()}` : '';
  window.history.pushState(null, '', `/a/${token}${suffix}${query}`);
};

const Badge = ({ tone, children, className = '' }: { tone: BadgeTone; children: React.ReactNode; className?: string }) => (
  <span className={`badge badge-${tone} ${className}`.trim()}>{children}</span>
);

const Card = ({ children, className = '' }: { children: React.ReactNode; className?: string }) => (
  <section className={`card ${className}`}>{children}</section>
);

const severityLabel = (severity: string) => {
  if (severity === 'critical') return 'Critical';
  if (severity === 'moderate') return 'Moderate';
  return 'Minor';
};

const severityTone = (severity: string): BadgeTone => {
  if (severity === 'critical') return 'critical';
  if (severity === 'moderate') return 'moderate';
  return 'minor';
};

const timeframeBuckets: Record<TimeframeBucketId, { label: string; shortLabel: string; tone: BadgeTone; accentClass: string }> = {
  beforeClose: { label: 'Before Close', shortLabel: 'Immediate', tone: 'critical', accentClass: 'critical' },
  nextMonth: { label: 'Next Month', shortLabel: '30 Days', tone: 'moderate', accentClass: 'accent' },
  afterMoveIn: { label: 'After Move In', shortLabel: '60-90 Days', tone: 'minor', accentClass: 'minor' },
  later: { label: 'Later', shortLabel: 'Future', tone: 'neutral', accentClass: 'neutral' },
};

const systemIconMap: Record<string, LucideIcon> = {
  structural: Building2,
  foundation: Building2,
  plumbing: Droplets,
  electrical: Zap,
  hvac: Fan,
  roof: House,
  interiorElements: Sofa,
  exteriorElements: Trees,
  safetySystems: ShieldCheck,
  appliances: PlugZap,
  miscellaneousSystems: Wrench,
};

const getSystemIcon = (systemId?: string): LucideIcon => systemIconMap[systemId || 'miscellaneousSystems'] || Wrench;

const houseMapAnchors: Record<string, HouseMapAnchor> = {
  hvac: { x: 435, y: 130, lineEndX: 12, lineEndY: 142, side: 'left' },
  plumbing: { x: 250, y: 305, lineEndX: 12, lineEndY: 310, side: 'left' },
  appliances: { x: 380, y: 440, lineEndX: 12, lineEndY: 394, side: 'left' },
  electrical: { x: 155, y: 430, lineEndX: 12, lineEndY: 226, side: 'left' },
  roof: { x: 820, y: 145, lineEndX: 978, lineEndY: 132, side: 'right' },
  exteriorElements: { x: 880, y: 420, lineEndX: 978, lineEndY: 210, side: 'right' },
  interiorElements: { x: 650, y: 445, lineEndX: 978, lineEndY: 288, side: 'right' },
  safetySystems: { x: 548, y: 350, lineEndX: 978, lineEndY: 478, side: 'right' },
  miscellaneousSystems: { x: 770, y: 445, lineEndX: 978, lineEndY: 522, side: 'right' },
  structural: { x: 360, y: 565, lineEndX: 978, lineEndY: 366, side: 'right' },
  foundation: { x: 525, y: 590, lineEndX: 978, lineEndY: 444, side: 'right' },
};

const houseMapHotspots: Record<string, HouseMapHotspot> = {
  roof: {
    label: 'Roof covering and flashing',
    d: 'M160 170 L495 20 L852 172 L837 205 L495 55 L175 205 Z',
  },
  hvac: {
    label: 'HVAC and attic',
    d: 'M245 170 L495 56 L746 170 Z',
  },
  plumbing: {
    label: 'Bathroom and plumbing',
    d: 'M220 190 H475 V345 H220 Z',
  },
  interiorElements: {
    label: 'Interior rooms and finishes',
    d: 'M490 190 H785 V345 H490 Z M490 355 H740 V535 H490 Z',
  },
  electrical: {
    label: 'Electrical panel and conduit',
    d: 'M125 355 H205 V560 H145 Q130 535 130 500 V445 H125 Z',
  },
  appliances: {
    label: 'Kitchen and laundry appliances',
    d: 'M220 355 H475 V535 H220 Z',
  },
  safetySystems: {
    label: 'Interior safety areas',
    d: 'M500 325 H570 V376 H500 Z',
  },
  exteriorElements: {
    label: 'Exterior side entry and yard',
    d: 'M785 335 L875 372 H940 V580 H785 Z',
  },
  structural: {
    label: 'Foundation and structural base',
    d: 'M190 535 H825 V610 H190 Z',
  },
  foundation: {
    label: 'Foundation',
    d: 'M190 535 H825 V610 H190 Z',
  },
  miscellaneousSystems: {
    label: 'Water heater, utility, and exterior misc',
    d: 'M740 355 H785 V535 H740 Z M0 475 H190 V622 H0 Z M825 475 H990 V622 H825 Z',
  },
};

const houseMapSystemOrder = [
  'hvac',
  'plumbing',
  'appliances',
  'electrical',
  'roof',
  'exteriorElements',
  'miscellaneousSystems',
  'interiorElements',
  'safetySystems',
  'structural',
  'foundation',
];

const normalizeTimeframeText = (timeframe?: string): string => (timeframe || '').toLowerCase().replace(/_/g, ' ');

const getTimeframeBucket = (issue: InspectionIssue): TimeframeBucketId => {
  const timeframe = normalizeTimeframeText(issue.derived?.normalizedTimeframe || issue.recommendedTimeframe);
  if (timeframe.includes('before close') || timeframe.includes('closing') || timeframe.includes('immediate') || timeframe.includes('urgent') || timeframe.includes('now')) return 'beforeClose';
  if (timeframe.includes('30') || timeframe.includes('month')) return 'nextMonth';
  if (timeframe.includes('60') || timeframe.includes('90') || timeframe.includes('2 months') || timeframe.includes('3 months')) return 'afterMoveIn';
  return 'later';
};

const getAssessment = (payload: InspectionReportPayload): { label: string; tone: BadgeTone } => {
  const categories = payload.rollups?.inspectionCategorization?.categories;
  const categoryList = Array.isArray(categories) ? categories.map(String) : [];
  if (categoryList.includes('reassess_purchase')) return { label: 'Reassess Purchase', tone: 'critical' };
  if (categoryList.includes('negotiate_before_closing')) return { label: 'Negotiate Before Closing', tone: 'moderate' };
  if (categoryList.includes('heavy_first_year_budget')) return { label: 'Heavy First-Year Budget', tone: 'moderate' };
  if (categoryList.includes('cosmetic_typical_condition')) return { label: 'Typical Condition', tone: 'success' };
  return { label: payload.summary.overallCondition || 'Review Findings', tone: 'neutral' };
};

const getHomeAge = (payload: InspectionReportPayload): number | null => {
  const built = Number.parseInt(String(payload.summary.yearBuilt || ''), 10);
  if (!Number.isFinite(built) || built <= 0) return null;
  return Math.max(0, new Date().getFullYear() - built);
};

const costTotalsForIssues = (issues: InspectionIssue[]) => issues.reduce(
  (totals, issue) => ({
    min: totals.min + issueCostMin(issue),
    max: totals.max + issueCostMax(issue),
    avg: totals.avg + issueCostAvg(issue),
  }),
  { min: 0, max: 0, avg: 0 },
);

const getSystemRiskSummaries = (payload: InspectionReportPayload) => {
  const systems = getSystemSummaries(payload).filter((system) => system.issueCount > 0);
  const issueMap = new Map(payload.issueList.map((issue) => [issue.issueNumber, issue]));

  return systems.map((system) => {
    const issues = system.issueNumbers.map((issueNumber) => issueMap.get(issueNumber)).filter(Boolean) as InspectionIssue[];
    const counts = issues.reduce(
      (acc, issue) => {
        const severity = normalizeSeverity(issue);
        acc[severity] += 1;
        if (getTimeframeBucket(issue) === 'beforeClose' || getTimeframeBucket(issue) === 'nextMonth') acc.urgent += 1;
        return acc;
      },
      { critical: 0, moderate: 0, minor: 0, urgent: 0 },
    );
    const worstCount = counts[system.worstSeverity];
    return { ...system, ...counts, worstCount };
  }).sort((a, b) => (
    severityRankValue(a.worstSeverity) - severityRankValue(b.worstSeverity)
    || b.worstCount - a.worstCount
    || b.urgent - a.urgent
    || b.costMax - a.costMax
    || b.issueCount - a.issueCount
  ));
};

const severityRankValue = (severity: string): number => {
  if (severity === 'critical') return 0;
  if (severity === 'moderate') return 1;
  return 2;
};

const getSystemsWithoutFindingsCount = (payload: InspectionReportPayload): number | null => {
  const issuesBySystemType = payload.rollups?.issuesBySystemType;
  if (!issuesBySystemType || typeof issuesBySystemType !== 'object' || Array.isArray(issuesBySystemType)) return null;
  return Object.values(issuesBySystemType).filter((issueNumbers) => Array.isArray(issueNumbers) && issueNumbers.length === 0).length;
};

const getSystemCostRows = (payload: InspectionReportPayload, issues = payload.issueList) => {
  const rows = new Map<string, { id: string; label: string; issues: InspectionIssue[]; min: number; max: number; avg: number; worstSeverity: ReturnType<typeof normalizeSeverity> }>();
  issues.forEach((issue) => {
    const id = issue.systemCategory || 'miscellaneousSystems';
    const current = rows.get(id) || {
      id,
      label: formatSystemLabel(id),
      issues: [],
      min: 0,
      max: 0,
      avg: 0,
      worstSeverity: 'minor' as ReturnType<typeof normalizeSeverity>,
    };
    current.issues.push(issue);
    current.min += issueCostMin(issue);
    current.max += issueCostMax(issue);
    current.avg += issueCostAvg(issue);
    const severity = normalizeSeverity(issue);
    current.worstSeverity = severityRankValue(severity) < severityRankValue(current.worstSeverity) ? severity : current.worstSeverity;
    rows.set(id, current);
  });
  return [...rows.values()].sort((a, b) => b.max - a.max || severityRankValue(a.worstSeverity) - severityRankValue(b.worstSeverity));
};

type SystemCostRow = ReturnType<typeof getSystemCostRows>[number];
type TimelineConcernGroup = {
  id: TimeframeBucketId;
  label: string;
  shortLabel: string;
  tone: BadgeTone;
  accentClass: string;
  issues: InspectionIssue[];
  systems: SystemCostRow[];
};

const getTimelinePrimaryConcern = (systems: SystemCostRow[], issues: InspectionIssue[]) => {
  const system = systems[0];
  if (system) {
    return {
      id: system.id,
      label: system.label,
      issueCount: system.issues.length,
      severity: system.worstSeverity,
    };
  }

  const issue = issues[0];
  const id = issue?.systemCategory || 'miscellaneousSystems';
  return {
    id,
    label: formatSystemLabel(id),
    issueCount: issues.length,
    severity: issue ? normalizeSeverity(issue) : 'minor',
  };
};

const getTimeframeCostRows = (issues: InspectionIssue[]) => {
  const rows = (Object.keys(timeframeBuckets) as TimeframeBucketId[]).map((id) => ({
    id,
    label: timeframeBuckets[id].label,
    tone: timeframeBuckets[id].tone,
    accentClass: timeframeBuckets[id].accentClass,
    issues: [] as InspectionIssue[],
    min: 0,
    max: 0,
  }));
  const byId = new Map(rows.map((row) => [row.id, row]));
  issues.forEach((issue) => {
    const row = byId.get(getTimeframeBucket(issue));
    if (!row) return;
    row.issues.push(issue);
    row.min += issueCostMin(issue);
    row.max += issueCostMax(issue);
  });
  return rows.filter((row) => row.issues.length > 0);
};

const getNegotiationRangeStatus = (): string => 'Estimates only. Actual costs may vary.';

const formatCompactCurrency = (value: number): string => {
  if (value >= 1000) {
    const compact = Math.trunc((value / 1000) * 10) / 10;
    const formatted = Number.isInteger(compact) ? compact.toString() : compact.toFixed(1);
    return `$${formatted}K`;
  }
  return formatCurrency(value);
};

const formatCompactCostRange = (min: number, max: number): string =>
  min === max ? formatCompactCurrency(min) : `${formatCompactCurrency(min)} - ${formatCompactCurrency(max)}`;

const formatStatusText = (value: string): string =>
  value
    .replace(/[_-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase());

const getSystemMapTone = (system: SystemSummary): HouseMapTone => {
  if (system.issueCount === 0) return 'neutral';
  if (system.worstSeverity === 'critical') return 'critical';
  if (system.worstSeverity === 'moderate') return 'moderate';
  if (system.worstSeverity === 'minor') return 'minor';
  return 'neutral';
};

const getSystemStatusLabel = (system: SystemSummary): string => {
  if (system.status) return formatStatusText(system.status);
  if (system.issueCount === 0) return 'No findings';
  return severityLabel(system.worstSeverity);
};

const formatInlineList = (items: string[]): string => {
  if (items.length <= 1) return items[0] || '';
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
};

function App() {
  const [page, setPage] = useState<Page>(loadInitialPage);
  const [menuOpen, setMenuOpen] = useState(false);
  const [report, setReport] = useState<AgentReportResponse>(sampleAgentReport);
  const [workspace, setWorkspace] = useState<WorkspaceState>(sampleAgentReport.workspaceState);
  const [buyerShare, setBuyerShare] = useState<BuyerShareState>(sampleAgentReport.buyerShareState);
  const [buyerReport, setBuyerReport] = useState<BuyerReportResponse | null>(null);
  const [updatedAt, setUpdatedAt] = useState<string | null>(sampleAgentReport.updatedAt || null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadingReport, setLoadingReport] = useState(false);

  const payload = buyerReport?.reportPayload || report.reportPayload;
  const apiBase = import.meta.env.VITE_SUPABASE_FUNCTIONS_URL as string | undefined;
  const routeToken = window.location.pathname.match(/^\/(?:a|share)\/([^/]+)/)?.[1];
  const analyticsCounts = getSeverityCounts(payload);

  useEffect(() => {
    if (page === 'buyer') return;
    initializeAnalytics();
  }, [page]);

  useEffect(() => {
    if (page === 'buyer') return;
    setAnalyticsContext({
      page: getAnalyticsPage(page),
      ui_variant: window.matchMedia('(max-width: 760px)').matches ? 'mobile' : 'desktop',
      report_shape: getReportShape(payload.issueList.length),
      severity_mix: getSeverityMix(analyticsCounts),
      selected_issue_count: workspace.negotiation_builder.selectedIssueNumbers.length,
    });
  }, [analyticsCounts.critical, analyticsCounts.minor, analyticsCounts.moderate, page, payload.issueList.length, workspace.negotiation_builder.selectedIssueNumbers.length]);

  useEffect(() => {
    if (page === 'buyer') return;
    captureAnalyticsEvent('dashboard_viewed', { entry_page: getAnalyticsPage(page) });
  }, [page]);

  useEffect(() => {
    if (!apiBase) return;
    if (!routeToken) {
      setLoadError('missing_token');
      return;
    }

    setLoadingReport(true);
    setLoadError(null);
    fetch(`${apiBase.replace(/\/$/, '')}/get-report`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: routeToken }),
    })
      .then((response) => (response.ok ? response.json() : Promise.reject(response)))
      .then((data: AgentReportResponse | BuyerReportResponse) => {
        if (data.role === 'agent') {
          setReport(data);
          setWorkspace(data.workspaceState);
          setBuyerShare(data.buyerShareState || defaultBuyerShareState);
          setBuyerReport(null);
          setUpdatedAt(data.updatedAt || null);
          setLoadError(null);
        } else {
          setBuyerReport(data);
          setBuyerShare({ ...defaultBuyerShareState, ...data.buyerShareState });
          setLoadError(null);
        }
      })
      .catch(() => {
        setLoadError('not_found');
      })
      .finally(() => {
        setLoadingReport(false);
      });
  }, [apiBase, routeToken]);

  if (apiBase && (loadingReport || (routeToken && !loadError && report.id === sampleAgentReport.id && !buyerReport))) {
    return <ReportLoadingMessage />;
  }

  if (apiBase && loadError) {
    return <ReportAccessMessage reason={loadError} />;
  }

  const setPageAndPath = (nextPage: Page, severity: IssueSeverity = 'all', issueNumber?: number) => {
    if (nextPage !== 'buyer') captureAnalyticsEvent('navigation_used', { destination: getAnalyticsPage(nextPage) });
    setPage(nextPage);
    updatePath(nextPage, severity, issueNumber);
    setMenuOpen(false);
  };

  if (page === 'buyer') {
    return <BuyerShareView payload={payload} workspace={buyerReport ? undefined : workspace} />;
  }

  return (
    <div className="app-shell">
      <TopBar payload={payload} onMenu={() => setMenuOpen(true)} />
      <DesktopSidebar page={page} payload={payload} workspace={workspace} onNavigate={setPageAndPath} />
      <MobileDrawer open={menuOpen} page={page} payload={payload} onClose={() => setMenuOpen(false)} onNavigate={setPageAndPath} />
      <main className="main-panel">
        {page === 'overview' && <OverviewPage payload={payload} workspace={workspace} onNavigate={setPageAndPath} />}
        {page === 'systems' && <SystemsPage payload={payload} onNavigate={setPageAndPath} />}
        {page === 'issues' && <IssuesPage payload={payload} workspace={workspace} setWorkspace={setWorkspace} onNavigate={setPageAndPath} />}
        {page === 'negotiation' && <NegotiationPage payload={payload} workspace={workspace} setWorkspace={setWorkspace} apiBase={apiBase} routeToken={routeToken} />}
        {page === 'client-prep' && (
          <ClientPrepPage
            payload={payload}
            workspace={workspace}
            setWorkspace={setWorkspace}
            buyerShare={buyerShare}
            setBuyerShare={setBuyerShare}
            apiBase={apiBase}
            routeToken={routeToken}
            updatedAt={updatedAt}
            setUpdatedAt={setUpdatedAt}
          />
        )}
      </main>
    </div>
  );
}

function ReportLoadingMessage() {
  return (
    <main className="access-page">
      <div className="brand-lockup">
        <div className="brand-mark">
          <Home size={16} />
        </div>
        <span>Clear Casa</span>
      </div>
      <Card>
        <div className="card-title-row">
          <Loader2 className="spin-icon" size={18} />
          <h1>Loading Report</h1>
        </div>
        <p className="muted-copy">Retrieving the secure inspection dashboard.</p>
      </Card>
    </main>
  );
}

function ReportAccessMessage({ reason }: { reason: string }) {
  const copy = reason === 'missing_token'
    ? 'Open the secure report link generated for this inspection.'
    : 'This report link is invalid, expired, revoked, or not available to this view.';

  return (
    <main className="access-page">
      <div className="brand-lockup">
        <div className="brand-mark">
          <Home size={16} />
        </div>
        <span>Clear Casa</span>
      </div>
      <Card>
        <div className="card-title-row">
          <ShieldCheck size={18} />
          <h1>Report Link Required</h1>
        </div>
        <p className="muted-copy">{copy}</p>
      </Card>
    </main>
  );
}

function TopBar({ payload, onMenu }: { payload: InspectionReportPayload; onMenu: () => void }) {
  return (
    <header className="top-bar">
      <button className="icon-button mobile-only" type="button" onClick={onMenu} aria-label="Open navigation">
        <Menu size={20} />
      </button>
      <div className="brand-lockup">
        <img className="brand-logo" src="/clearcasa_logo/CC_Logo.svg" alt="Clear Casa" />
      </div>
      <div className="mobile-title ph-no-capture">
        <strong>{payload.summary.address || 'Inspection report'}</strong>
        <span>{payload.summary.propertyType || 'Inspection report'}</span>
      </div>
    </header>
  );
}

function DesktopSidebar({
  page,
  payload,
  workspace,
  onNavigate,
}: {
  page: Page;
  payload: InspectionReportPayload;
  workspace: WorkspaceState;
  onNavigate: (page: Page) => void;
}) {
  const totals = getNegotiationTotals(payload, workspace);
  return (
    <aside className="sidebar desktop-only">
      <div className="property-block ph-no-capture">
        <span>Property</span>
        <strong>{payload.summary.address || 'Address not listed'}</strong>
        <small>{payload.summary.propertyType || 'Property type not listed'}</small>
      </div>
      <nav className="nav-list">
        {navItems.map(({ page: navPage, label, icon: Icon }) => (
          <button key={navPage} className={`nav-link ${page === navPage ? 'active' : ''}`} type="button" onClick={() => onNavigate(navPage)}>
            <Icon size={16} />
            <span>{label}</span>
            {navPage === 'issues' && <em>{payload.issueList.length}</em>}
            {navPage === 'negotiation' && <em>{totals.selected.length}</em>}
          </button>
        ))}
      </nav>
    </aside>
  );
}

function MobileDrawer({
  open,
  page,
  payload,
  onClose,
  onNavigate,
}: {
  open: boolean;
  page: Page;
  payload: InspectionReportPayload;
  onClose: () => void;
  onNavigate: (page: Page) => void;
}) {
  if (!open) return null;
  return (
    <div className="drawer-backdrop">
      <aside className="drawer">
        <div className="drawer-head">
          <div className="brand-lockup">
            <div className="brand-mark">
              <Home size={16} />
            </div>
            <span>Clear Casa</span>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Close navigation">
            <X size={20} />
          </button>
        </div>
        <div className="property-block ph-no-capture">
          <span>Property</span>
          <strong>{payload.summary.address || 'Address not listed'}</strong>
        </div>
        <nav className="nav-list">
          {navItems.map(({ page: navPage, label, icon: Icon }) => (
            <button key={navPage} className={`nav-link ${page === navPage ? 'active' : ''}`} type="button" onClick={() => onNavigate(navPage)}>
              <Icon size={16} />
              <span>{label}</span>
            </button>
          ))}
        </nav>
      </aside>
    </div>
  );
}

function OverviewPage({
  payload,
  workspace,
  onNavigate,
}: {
  payload: InspectionReportPayload;
  workspace: WorkspaceState;
  onNavigate: (page: Page, severity?: IssueSeverity) => void;
}) {
  const counts = getSeverityCounts(payload);
  const totalIssues = payload.issueList.length;
  const assessment = getAssessment(payload);
  const reportPosture = getReportPosture(payload);
  const systemSummaries = getSystemSummaries(payload);
  const systemsWithFindingsCount = systemSummaries.filter((system) => system.issueCount > 0).length;
  const negotiationTotals = getNegotiationTotals(payload, workspace);
  const selectedIssues = negotiationTotals.selected;
  const sortedIssues = getIssuesSortedByPriority(payload.issueList);
  const negotiationRangeStatus = getNegotiationRangeStatus();
  const overallCondition = payload.summary.overallCondition?.trim()
    || 'No overall condition summary was included with this inspection report.';
  const desktopTimeframeGroups: TimelineConcernGroup[] = (Object.keys(timeframeBuckets) as TimeframeBucketId[]).map((id) => {
    const issues = sortedIssues.filter((issue) => getTimeframeBucket(issue) === id);
    return {
      id,
      ...timeframeBuckets[id],
      issues,
      systems: getSystemCostRows(payload, issues).slice(0, 3),
    };
  }).filter((group) => group.issues.length > 0);
  const severityTotal = Math.max(counts.critical + counts.moderate + counts.minor, totalIssues, 1);
  const criticalDeg = (counts.critical / severityTotal) * 360;
  const moderateDeg = ((counts.critical + counts.moderate) / severityTotal) * 360;
  const reportJobs: ReportJobCardProps[] = [
    {
      icon: House,
      tone: 'systems',
      title: 'Systems',
      job: 'Assess the home by system',
      detail: 'Show the buyer where the house is carrying attention, cost, and condition risk.',
      metric: `${systemsWithFindingsCount} systems with findings`,
      actionLabel: 'View systems',
      page: 'systems',
    },
    {
      icon: ClipboardCheck,
      tone: 'issues',
      title: 'All Issues',
      job: 'Prioritize every finding',
      detail: 'Sort the inspection into what matters now, what can wait, and what needs follow-up.',
      metric: `${totalIssues} findings`,
      actionLabel: 'Review findings',
      page: 'issues',
    },
    {
      icon: Wrench,
      tone: 'negotiation',
      title: 'BINSR Draft',
      job: 'Build the BINSR draft',
      detail: 'Choose the report findings to carry into a buyer-reviewed BINSR support draft.',
      metric: selectedIssues.length ? `${selectedIssues.length} included` : 'No findings included',
      actionLabel: 'Open BINSR draft',
      page: 'negotiation',
    },
    {
      icon: MessageSquareText,
      tone: 'client',
      title: 'Client Prep',
      job: 'Prepare the buyer conversation',
      detail: 'Translate the report into talking points, questions, and next-step guidance.',
      metric: `${counts.critical} critical to explain`,
      actionLabel: 'Prep client',
      page: 'client-prep',
    },
  ];

  return (
    <div className="page-stack overview-page">
      <PageHeader title="Inspection Overview" subtitle={`${payload.summary.inspectionDate || 'Date not listed'} · Built ${payload.summary.yearBuilt || 'not listed'} · ${payload.summary.squareFeet || 0} sq ft`} />

      <Card className={`mobile-overview-hero readout-${assessment.tone}`}>
        <div className="card-title-split">
          <IconTitle icon={ShieldCheck} title="Agent readout" />
        </div>
        <div className="readout-title-row">
          <span className="readout-source-label">Inspection category</span>
          <h2>{reportPosture}</h2>
          <p>{overallCondition}</p>
        </div>
      </Card>

      <Card className="mobile-severity-card">
        <div className="card-title-split">
          <IconTitle icon={AlertTriangle} title="Severity Mix" />
          <strong>{counts.critical} critical</strong>
        </div>
        <button
          type="button"
          className="severity-donut"
          style={{
            background: `conic-gradient(var(--cc-critical) 0deg ${criticalDeg}deg, var(--cc-moderate) ${criticalDeg}deg ${moderateDeg}deg, var(--cc-minor) ${moderateDeg}deg 360deg)`,
          }}
          aria-label={`View all ${totalIssues} issues: ${counts.critical} critical, ${counts.moderate} moderate, ${counts.minor} minor`}
          onClick={() => {
            captureAnalyticsEvent('overview_chart_opened', { severity: 'all' });
            onNavigate('issues');
          }}
        >
          <span>{totalIssues}</span>
        </button>
        <SeverityPills counts={counts} onSelect={(severity) => {
          captureAnalyticsEvent('overview_chart_opened', { severity });
          onNavigate('issues', severity);
        }} />
      </Card>

      <Card className="mobile-ask-card">
        <div className="card-title-split">
          <IconTitle icon={DollarSign} title="Recommended Negotiation Range" />
        </div>
        <NegotiationStrategyBand totals={negotiationTotals} />
        <p className="micro-copy">{negotiationRangeStatus}</p>
      </Card>

      <div className="desktop-summary-grid">
        <Card className={`assessment-card agent-readout-card readout-${assessment.tone}`}>
          <div className="card-title-split">
            <IconTitle icon={ShieldCheck} title="Agent readout" />
          </div>
          <div className="readout-title-row">
            <span className="readout-source-label">Inspection category</span>
            <h2>{reportPosture}</h2>
            <p>{overallCondition}</p>
          </div>
          <button className="text-link action-link" type="button" onClick={() => {
            captureAnalyticsEvent('overview_card_opened', { destination: 'systems' });
            onNavigate('systems');
          }}>
            <span>Review systems</span>
            <ArrowRight size={14} aria-hidden="true" />
          </button>
        </Card>

        <Card className="severity-card">
          <div className="card-title-split">
            <IconTitle icon={AlertTriangle} title="Severity Mix" />
            <strong>{counts.critical} critical</strong>
          </div>
          <button
            type="button"
            className="severity-donut"
            style={{
              background: `conic-gradient(var(--cc-critical) 0deg ${criticalDeg}deg, var(--cc-moderate) ${criticalDeg}deg ${moderateDeg}deg, var(--cc-minor) ${moderateDeg}deg 360deg)`,
            }}
            aria-label={`View all ${totalIssues} issues: ${counts.critical} critical, ${counts.moderate} moderate, ${counts.minor} minor`}
            onClick={() => {
              captureAnalyticsEvent('overview_chart_opened', { severity: 'all' });
              onNavigate('issues');
            }}
          >
            <span>{totalIssues}</span>
          </button>
          <SeverityPills counts={counts} onSelect={(severity) => {
            captureAnalyticsEvent('overview_chart_opened', { severity });
            onNavigate('issues', severity);
          }} />
        </Card>

        <Card className="ask-card">
          <div className="card-title-split">
            <IconTitle icon={DollarSign} title="Recommended Negotiation Range" />
          </div>
          <NegotiationStrategyBand totals={negotiationTotals} />
          <p className="micro-copy">{negotiationRangeStatus}</p>
          <button className="text-link action-link" type="button" onClick={() => {
            captureAnalyticsEvent('overview_card_opened', { destination: 'binsr_draft' });
            onNavigate('negotiation');
          }}>
            <span>Open BINSR draft</span>
            <ArrowRight size={14} aria-hidden="true" />
          </button>
        </Card>
      </div>

      <div className="overview-middle-grid">
        <Card className="top-concerns-card">
          <div className="section-heading">
            <div>
              <IconTitle icon={Clock3} title="Timeline Triage" />
            </div>
            <ChevronRight size={16} />
          </div>
          <div className={`timeline-grid timeline-grid--${desktopTimeframeGroups.length}`}>
            {desktopTimeframeGroups.map((group) => (
              <TimelineTriageBucket key={group.id} group={group} />
            ))}
          </div>
          <div className="mobile-priority-list">
            {desktopTimeframeGroups.map((group) => (
              <TimelineTriageBucket key={group.id} group={group} variant="mobile" />
            ))}
          </div>
          <div className="subtle-footer">
            <button className="text-link action-link" type="button" onClick={() => {
              captureAnalyticsEvent('work_report_link_opened', { destination: 'issues' });
              onNavigate('issues');
            }}>
              <span>View all {totalIssues} issues</span>
              <ArrowRight size={14} aria-hidden="true" />
            </button>
          </div>
        </Card>

        <HomeSystemsMapCard
          systems={systemSummaries}
          onNavigate={onNavigate}
        />

      </div>

      <div className="overview-cost-grid">
        <Card className="agent-next-steps-card report-jobs-card">
          <div className="section-heading">
            <div>
              <IconTitle icon={PackageCheck} title="Work the Report" />
              <p>Open the section that matches the job the agent needs to get done.</p>
            </div>
            <ChevronRight size={16} />
          </div>
          <div className="report-job-grid">
            {reportJobs.map((job) => (
              <ReportJobCard key={job.page} {...job} onNavigate={onNavigate} />
            ))}
          </div>
        </Card>
      </div>

      <div className="mobile-bottom-actions">
        <button type="button" onClick={() => {
          captureAnalyticsEvent('work_report_link_opened', { destination: 'client_prep' });
          onNavigate('client-prep');
        }}>
          <Share2 size={16} />
          Client Prep
        </button>
        <button type="button" onClick={() => {
          captureAnalyticsEvent('work_report_link_opened', { destination: 'binsr_draft' });
          onNavigate('negotiation');
        }}>
          <Hammer size={16} />
          BINSR Draft
        </button>
      </div>
    </div>
  );
}

const getOrderedHouseMapSystems = (systems: SystemSummary[]): SystemSummary[] => {
  const systemMap = new Map(systems.map((system) => [system.id, system]));
  const orderedSystems = houseMapSystemOrder
    .map((systemId) => systemMap.get(systemId))
    .filter((system): system is SystemSummary => Boolean(system));
  const anchoredIds = new Set(orderedSystems.map((system) => system.id));
  const remainingSystems = systems.filter((system) => !anchoredIds.has(system.id) && houseMapAnchors[system.id]);
  return [...orderedSystems, ...remainingSystems];
};

function HomeSystemsMapCard({
  systems,
  onNavigate,
}: {
  systems: SystemSummary[];
  onNavigate: (page: Page) => void;
}) {
  const orderedSystems = useMemo(() => getOrderedHouseMapSystems(systems), [systems]);
  const [selectedSystemId, setSelectedSystemId] = useState(
    orderedSystems.find((system) => system.issueCount > 0)?.id || orderedSystems[0]?.id || '',
  );
  const layoutRef = useRef<HTMLDivElement | null>(null);
  const frameRef = useRef<HTMLDivElement | null>(null);
  const cardRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [connectorLines, setConnectorLines] = useState<HouseMapConnectorLine[]>([]);
  const leftSystems = orderedSystems.filter((system) => houseMapAnchors[system.id]?.side === 'left');
  const rightSystems = orderedSystems.filter((system) => houseMapAnchors[system.id]?.side === 'right');
  const orderedSystemIds = orderedSystems.map((system) => system.id).join('|');
  const selectHomeContext = (systemId: string) => {
    captureAnalyticsEvent('home_context_opened', { context_area: systemId });
    setSelectedSystemId(systemId);
  };

  useLayoutEffect(() => {
    const layout = layoutRef.current;
    const frame = frameRef.current;
    if (!layout || !frame) return undefined;

    let animationFrame = 0;
    const updateConnectorLines = () => {
      cancelAnimationFrame(animationFrame);
      animationFrame = requestAnimationFrame(() => {
        const layoutRect = layout.getBoundingClientRect();
        const frameRect = frame.getBoundingClientRect();
        if (!layoutRect.width || !frameRect.width || !frameRect.height) {
          setConnectorLines([]);
          return;
        }

        const nextLines = orderedSystems.flatMap((system) => {
          const anchor = houseMapAnchors[system.id];
          const card = cardRefs.current[system.id];
          if (!anchor || !card) return [];

          const cardRect = card.getBoundingClientRect();
          if (!cardRect.width || !cardRect.height) return [];

          const startX = anchor.side === 'left'
            ? cardRect.right - layoutRect.left
            : cardRect.left - layoutRect.left;
          const startY = cardRect.top - layoutRect.top + cardRect.height / 2;
          const endX = frameRect.left - layoutRect.left + (anchor.x / 990) * frameRect.width;
          const endY = frameRect.top - layoutRect.top + (anchor.y / 622) * frameRect.height;
          const distance = Math.abs(endX - startX);
          const curve = Math.max(36, Math.min(180, distance * 0.42));
          const controlOneX = anchor.side === 'left' ? startX + curve : startX - curve;
          const controlTwoX = anchor.side === 'left' ? endX - curve : endX + curve;

          return [{
            id: system.id,
            tone: getSystemMapTone(system),
            path: `M ${startX} ${startY} C ${controlOneX} ${startY} ${controlTwoX} ${endY} ${endX} ${endY}`,
            startX,
            startY,
          }];
        });

        setConnectorLines(nextLines);
      });
    };

    updateConnectorLines();
    const observer = new ResizeObserver(updateConnectorLines);
    observer.observe(layout);
    observer.observe(frame);
    Object.values(cardRefs.current).forEach((card) => {
      if (card) observer.observe(card);
    });
    window.addEventListener('resize', updateConnectorLines);

    return () => {
      cancelAnimationFrame(animationFrame);
      observer.disconnect();
      window.removeEventListener('resize', updateConnectorLines);
    };
  }, [orderedSystemIds, orderedSystems]);

  return (
    <Card className="system-risk-card home-map-card">
      <div className="section-heading">
        <div>
          <IconTitle icon={Home} title="Home Context" />
        </div>
        <ChevronRight size={16} />
      </div>
      <div ref={layoutRef} className="home-map-layout" aria-label="System findings mapped to house areas">
        <div className="home-map-callouts home-map-callouts-left">
          {leftSystems.map((system) => (
            <SystemMapCallout
              key={system.id}
              system={system}
              selected={system.id === selectedSystemId}
              onSelect={selectHomeContext}
              systemRef={(node) => {
                cardRefs.current[system.id] = node;
              }}
            />
          ))}
        </div>
        <HouseMapConnectors lines={connectorLines} selectedSystemId={selectedSystemId} />
        <HouseCutawaySvg
          systems={orderedSystems}
          selectedSystemId={selectedSystemId}
          onSelectSystem={selectHomeContext}
          frameRef={frameRef}
        />
        <div className="home-map-callouts home-map-callouts-right">
          {rightSystems.map((system) => (
            <SystemMapCallout
              key={system.id}
              system={system}
              selected={system.id === selectedSystemId}
              onSelect={selectHomeContext}
              systemRef={(node) => {
                cardRefs.current[system.id] = node;
              }}
            />
          ))}
        </div>
      </div>
      <div className="mobile-home-system-list">
        {orderedSystems.map((system) => (
          <SystemMapCallout
            key={system.id}
            system={system}
            selected={system.id === selectedSystemId}
            onSelect={selectHomeContext}
            compact
          />
        ))}
      </div>
      <div className="subtle-footer">
        <button className="text-link action-link" type="button" onClick={() => {
          captureAnalyticsEvent('overview_card_opened', { destination: 'systems' });
          onNavigate('systems');
        }}>
          <span>View systems</span>
          <ArrowRight size={14} aria-hidden="true" />
        </button>
      </div>
    </Card>
  );
}

function SystemMapCallout({
  system,
  selected,
  onSelect,
  systemRef,
  compact = false,
}: {
  system: SystemSummary;
  selected: boolean;
  onSelect: (systemId: string) => void;
  systemRef?: (node: HTMLButtonElement | null) => void;
  compact?: boolean;
}) {
  const Icon = getSystemIcon(system.id);
  const tone = getSystemMapTone(system);
  const issueLabel = system.issueCount === 1 ? '1 issue' : `${system.issueCount} issues`;
  const rangeLabel = system.issueCount ? formatCompactCostRange(system.costMin, system.costMax) : '$0';

  return (
    <button
      ref={systemRef}
      type="button"
      className={`home-system-callout map-tone-${tone} ${selected ? 'is-selected' : ''} ${compact ? 'is-compact' : ''}`}
      data-system-id={system.id}
      onClick={() => onSelect(system.id)}
      aria-pressed={selected}
      aria-label={`Highlight ${system.label} on the home visual`}
    >
      <span className="home-callout-icon">
        <Icon size={16} />
      </span>
      <span className="home-callout-body">
        <span className="home-callout-title-row">
          <strong>{system.label}</strong>
          <em>{getSystemStatusLabel(system)}</em>
        </span>
        <span className="home-callout-meta">
          <span>{issueLabel}</span>
          <span>{rangeLabel}</span>
        </span>
      </span>
    </button>
  );
}

function HouseMapConnectors({
  lines,
  selectedSystemId,
}: {
  lines: HouseMapConnectorLine[];
  selectedSystemId: string;
}) {
  const selectedLine = lines.find((line) => line.id === selectedSystemId);
  if (!selectedLine) return null;

  return (
    <svg className="house-layout-connectors" aria-hidden="true">
      <g className={`house-layout-connector map-tone-${selectedLine.tone} is-selected`}>
        <path d={selectedLine.path} />
        <circle cx={selectedLine.startX} cy={selectedLine.startY} r="4.5" />
      </g>
    </svg>
  );
}

function HouseCutawaySvg({
  systems,
  selectedSystemId,
  onSelectSystem,
  frameRef,
}: {
  systems: SystemSummary[];
  selectedSystemId: string;
  onSelectSystem: (systemId: string) => void;
  frameRef: RefObject<HTMLDivElement | null>;
}) {
  const anchoredSystems = systems.filter((system) => houseMapAnchors[system.id]);

  return (
    <div ref={frameRef} className="house-cutaway-frame">
      <picture className="house-cutaway-picture" aria-hidden="true">
        <source srcSet="/house-cutaway/house-cutaway-base.webp" type="image/webp" />
        <img className="house-cutaway-image" src="/house-cutaway/house-cutaway-base.png" alt="" />
      </picture>
      <svg className="house-cutaway-svg" viewBox="0 0 990 622" role="img" aria-labelledby="house-map-title">
        <title id="house-map-title">House systems map</title>
        <g className="house-hotspot-layer">
          {anchoredSystems.map((system) => {
            const hotspot = houseMapHotspots[system.id];
            if (!hotspot) return null;
            const selected = system.id === selectedSystemId;
            return (
              <g
                key={`hotspot-${system.id}`}
                className={`house-hotspot-group map-tone-${getSystemMapTone(system)} ${selected ? 'is-selected' : ''}`}
                role="button"
                tabIndex={0}
                aria-label={`Highlight ${hotspot.label}`}
                aria-pressed={selected}
                onClick={() => onSelectSystem(system.id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onSelectSystem(system.id);
                  }
                }}
              >
                <path className="house-hotspot" d={hotspot.d} />
              </g>
            );
          })}
        </g>
        <g className="house-anchor-layer" aria-hidden="true">
          {anchoredSystems.map((system) => {
            const anchor = houseMapAnchors[system.id];
            return (
              <g key={`anchor-${system.id}`} className={`house-anchor map-tone-${getSystemMapTone(system)} ${system.id === selectedSystemId ? 'is-selected' : ''}`}>
                <circle className="house-anchor-ring" cx={anchor.x} cy={anchor.y} r="10" />
                <circle className="house-anchor-dot" cx={anchor.x} cy={anchor.y} r="5" />
              </g>
            );
          })}
        </g>
      </svg>
    </div>
  );
}

function SystemsPage({
  payload,
  onNavigate,
}: {
  payload: InspectionReportPayload;
  onNavigate: (page: Page, severity?: IssueSeverity, issueNumber?: number) => void;
}) {
  const systems = getSystemSummaries(payload);
  const sortedSystems = sortSystemsForQuickView(systems);
  const [selectedSystemId, setSelectedSystemId] = useState(sortedSystems[0]?.id || '');
  const issueMap = new Map(payload.issueList.map((issue) => [issue.issueNumber, issue]));
  const issueSystems = systems.filter((system) => system.issueCount > 0);
  const totalIssueCosts = costTotalsForIssues(payload.issueList);
  const homeAge = getHomeAge(payload);
  const selectedSystem = systems.find((system) => system.id === selectedSystemId) || sortedSystems[0];
  const selectSystem = (systemId: string) => {
    captureAnalyticsEvent('system_selected', { system_category: systemId });
    setSelectedSystemId(systemId);
  };

  return (
    <div className="page-stack systems-page">
      <PageHeader
        title="Systems Overview"
        subtitle={`${systems.length} systems inspected${payload.summary.yearBuilt ? ` · Built ${payload.summary.yearBuilt}` : ''}${homeAge === null ? '' : ` · ${homeAge} years old`}`}
      />

      <div className="system-mobile-summary">
        <span>{issueSystems.length} systems need work</span>
        <strong>{formatCompactCostRange(totalIssueCosts.min, totalIssueCosts.max)}</strong>
      </div>

      <div className="systems-focus-grid">
        <SystemConditionMapCard systems={systems} selectedSystemId={selectedSystem?.id || ''} onSelectSystem={selectSystem} />
        {selectedSystem && (
          <SelectedSystemPanel system={selectedSystem} issueMap={issueMap} onNavigate={onNavigate} />
        )}
      </div>
    </div>
  );
}

const systemDescriptorMap: Record<string, string> = {
  structural: 'Foundation, framing, and support',
  plumbing: 'Water supply and drainage',
  electrical: 'Panel, wiring, and outlets',
  hvac: 'Heating and cooling',
  roof: 'Roofing, flashing, and drainage',
  interiorElements: 'Interior finishes and openings',
  exteriorElements: 'Exterior surfaces and site items',
  safetySystems: 'Life safety and hazard controls',
  appliances: 'Installed appliances',
  miscellaneousSystems: 'Other inspected systems',
};

const systemTimeframeRank: Record<TimeframeBucketId, number> = {
  beforeClose: 0,
  nextMonth: 1,
  afterMoveIn: 2,
  later: 3,
};

const systemTimeframeLabels: Record<TimeframeBucketId, string> = {
  beforeClose: 'Before close',
  nextMonth: 'Next 30 days',
  afterMoveIn: 'After move in',
  later: 'Later',
};

const categoryAliases: Record<string, string[]> = {
  critical_safety: ['critical_safety'],
  surprise_cost: ['surprise_cost'],
  negotiation_leverage: ['negotiation_leverage', 'negotiation_item'],
  future_project: ['future_project'],
  diy: ['diy', 'diy_candidate'],
  monitoring: ['monitoring', 'monitor'],
};

const tagAliases: Record<string, string[]> = {
  urgent_repair: ['urgent_repair', 'urgent'],
  specialist_required: ['specialist_required', 'specialist'],
  not_typical: ['not_typical', 'uncommon_issue'],
  high_cost: ['high_cost'],
};

const getSystemDescriptor = (systemId: string): string => systemDescriptorMap[systemId] || 'Inspected system';

const getIssuesForSystem = (system: SystemSummary, issueMap: Map<number, InspectionIssue>): InspectionIssue[] =>
  system.issueNumbers.map((issueNumber) => issueMap.get(issueNumber)).filter(Boolean) as InspectionIssue[];

const getIssueCategories = (issue: InspectionIssue): string[] =>
  (issue.derived?.categories || []).map((category) => category.toLowerCase());

const getIssueTagNames = (issue: InspectionIssue): string[] =>
  (issue.derived?.tags || []).map((tag) => tag.name.toLowerCase());

const issueHasCategory = (issue: InspectionIssue, key: string): boolean => {
  const aliases = categoryAliases[key] || [key];
  const categories = getIssueCategories(issue);
  return aliases.some((alias) => categories.includes(alias));
};

const issueHasTag = (issue: InspectionIssue, key: string): boolean => {
  const aliases = tagAliases[key] || [key];
  const tags = getIssueTagNames(issue);
  return aliases.some((alias) => tags.includes(alias));
};

const issueNeedsSpecialistReview = (issue: InspectionIssue): boolean => Boolean(
  issue.specialist_evaluation_needed
  || issue.derived?.specialistNeeded
  || issue.specialist_type
  || issueHasTag(issue, 'specialist_required'),
);

const issueIsSafetyOrUrgent = (issue: InspectionIssue): boolean => (
  issueIsSafety(issue)
  || issueHasCategory(issue, 'critical_safety')
  || issueHasTag(issue, 'urgent_repair')
  || getTimeframeBucket(issue) === 'beforeClose'
);

const issueIsHighCost = (issue: InspectionIssue): boolean => (
  issueCostAvg(issue) >= 800
  || issueHasTag(issue, 'high_cost')
  || issueHasCategory(issue, 'surprise_cost')
);

const issueIsNegotiationCandidate = (issue: InspectionIssue, selected: Set<number>): boolean => (
  selected.size > 0
    ? selected.has(issue.issueNumber)
    : normalizeSeverity(issue) === 'critical'
  || issueIsSafetyOrUrgent(issue)
  || issueNeedsSpecialistReview(issue)
  || issueIsHighCost(issue)
  || issueHasCategory(issue, 'negotiation_leverage')
);

const issueNeedsBeforeCloseVerification = (issue: InspectionIssue): boolean => (
  issueIsSafetyOrUrgent(issue)
  || issueNeedsSpecialistReview(issue)
);

const issueIsRequestCandidate = (issue: InspectionIssue): boolean => (
  issueIsNegotiationCandidate(issue, new Set())
  || issueHasTag(issue, 'not_typical')
  || issue.derived?.typicalForAge === false
  || issue.typical_for_age?.typical === false
);

const issueIsMaintenanceItem = (issue: InspectionIssue): boolean => (
  issueHasCategory(issue, 'future_project')
  || issueHasCategory(issue, 'diy')
  || issueHasCategory(issue, 'monitoring')
  || getTimeframeBucket(issue) === 'afterMoveIn'
  || getTimeframeBucket(issue) === 'later'
);

const issueNeedsAgentReview = (issue: InspectionIssue): boolean => (
  issueNeedsBeforeCloseVerification(issue)
  || issueIsRequestCandidate(issue)
);

const getIssueSearchText = (issue: InspectionIssue): string => [
  issue.issueNumber,
  issue.title,
  issue.description,
  issue.location,
  issue.nspireLocation,
  issue.systemCategory,
  formatSystemLabel(issue.systemCategory || 'miscellaneousSystems'),
  issue.source,
  issue.fileRecco,
  issue.aiRecco,
  issue.potential_consequences,
  issue.repair_approach,
  issue.recommendedTimeframe,
  issue.typicalBuyerAction,
  issue.specialist_type,
  ...getIssueCategories(issue),
  ...getIssueTagNames(issue),
  ...(issue.derived?.tags || []).map((tag) => tag.value),
].filter(Boolean).join(' ').toLowerCase();

const getIssueActionLabel = (issue: InspectionIssue): string => {
  if (issueNeedsSpecialistReview(issue)) return `Get ${issue.specialist_type || 'specialist'} input`;
  if (issueIsSafetyOrUrgent(issue)) return 'Prioritize before close';
  if (issueHasCategory(issue, 'negotiation_leverage')) return 'Seller request candidate';
  if (issueIsHighCost(issue)) return 'Review credit or budget';
  if (issueIsMaintenanceItem(issue)) return 'Plan buyer budget';
  return issue.typicalBuyerAction || 'Review with buyer';
};

const getIssueImpactText = (issue: InspectionIssue): string =>
  issue.potential_consequences
  || issue.severity?.explanation
  || issue.safety?.explanation
  || issue.description
  || 'No consequence detail was extracted.';

const getIssueRecommendationText = (issue: InspectionIssue): string =>
  issue.aiRecco
  || issue.fileRecco
  || issue.typicalBuyerAction
  || issue.repair_approach
  || 'Review this finding with the buyer and decide whether it belongs in the request package.';

const getIssueTypicalLabel = (issue: InspectionIssue): string => {
  const typical = issue.derived?.typicalForAge ?? issue.typical_for_age?.typical;
  if (typical === false) return 'Not typical for age';
  if (typical === true) return 'Typical for age';
  return 'Age context not flagged';
};

const getIssueSpecialistLabel = (issue: InspectionIssue): string =>
  issueNeedsSpecialistReview(issue) ? issue.specialist_type || 'Specialist review' : 'No specialist flagged';

const sortIssuesForMode = (issues: InspectionIssue[], mode: IssueSortMode): InspectionIssue[] => {
  const prioritySorted = getIssuesSortedByPriority(issues);
  if (mode === 'cost') return [...prioritySorted].sort((a, b) => issueCostAvg(b) - issueCostAvg(a));
  if (mode === 'system') return [...prioritySorted].sort((a, b) => (
    formatSystemLabel(a.systemCategory || 'miscellaneousSystems').localeCompare(formatSystemLabel(b.systemCategory || 'miscellaneousSystems'))
    || a.issueNumber - b.issueNumber
  ));
  if (mode === 'timeframe') return [...prioritySorted].sort((a, b) => (
    systemTimeframeRank[getTimeframeBucket(a)] - systemTimeframeRank[getTimeframeBucket(b)]
    || a.issueNumber - b.issueNumber
  ));
  return prioritySorted;
};

const getInspectionCategories = (payload: InspectionReportPayload): string[] => {
  const categorization = payload.rollups?.inspectionCategorization;
  if (!categorization) return [];
  const categories = categorization.categories;
  if (Array.isArray(categories)) return categories.map(String);
  return Object.entries(categorization)
    .filter(([key, value]) => key !== 'categories' && Boolean(value))
    .map(([key]) => key);
};

const getReportPosture = (payload: InspectionReportPayload): string => {
  const categories = getInspectionCategories(payload);
  if (categories.includes('reassess_purchase') && categories.includes('negotiate_before_closing')) return 'Reassess and negotiate before closing';
  if (categories.includes('reassess_purchase')) return 'Reassess before waiving inspection';
  if (categories.includes('negotiate_before_closing')) return 'Negotiate before closing';
  if (categories.includes('heavy_first_year_budget')) return 'Plan for heavy first-year budget';
  if (categories.includes('cosmetic_typical_condition')) return 'Mostly typical condition';
  return 'Review systems before next steps';
};

const getSystemsDecisionActions = (payload: InspectionReportPayload): string[] => {
  const categories = getInspectionCategories(payload);
  const actions: string[] = [];
  const hasVerifySignals = payload.issueList.some((issue) => (
    issueHasCategory(issue, 'critical_safety')
    || issueHasTag(issue, 'urgent_repair')
    || issueHasTag(issue, 'specialist_required')
    || issue.specialist_evaluation_needed
    || getTimeframeBucket(issue) === 'beforeClose'
    || getTimeframeBucket(issue) === 'nextMonth'
  ));
  const hasRequestSignals = categories.includes('negotiate_before_closing') || payload.issueList.some((issue) => (
    issueHasCategory(issue, 'negotiation_leverage')
    || issueHasCategory(issue, 'surprise_cost')
    || issueHasTag(issue, 'not_typical')
    || issueHasTag(issue, 'specialist_required')
    || issue.specialist_evaluation_needed
  ));
  const hasBudgetSignals = categories.includes('heavy_first_year_budget') || payload.issueList.some((issue) => (
    issueHasCategory(issue, 'future_project')
    || getTimeframeBucket(issue) === 'afterMoveIn'
    || getTimeframeBucket(issue) === 'later'
  ));

  if (hasVerifySignals) actions.push('Verify before close');
  if (hasRequestSignals) actions.push('Build request package');
  if (hasBudgetSignals) actions.push('Set first-year reserve');
  return actions.length ? actions.slice(0, 3) : ['Review with buyer'];
};

const getSystemDriverSummary = (issues: InspectionIssue[]) => {
  const criticalSafety = issues.filter((issue) => issueHasCategory(issue, 'critical_safety')).length;
  const urgent = issues.filter((issue) => issueHasTag(issue, 'urgent_repair') || getTimeframeBucket(issue) === 'beforeClose' || getTimeframeBucket(issue) === 'nextMonth').length;
  const specialist = issues.filter((issue) => issueHasTag(issue, 'specialist_required') || issue.specialist_evaluation_needed).length;
  const surpriseCost = issues.filter((issue) => issueHasCategory(issue, 'surprise_cost')).length;
  const negotiation = issues.filter((issue) => issueHasCategory(issue, 'negotiation_leverage')).length;
  const notTypical = issues.filter((issue) => issueHasTag(issue, 'not_typical') || issue.derived?.typicalForAge === false || issue.typical_for_age?.typical === false).length;
  const futureProject = issues.filter((issue) => issueHasCategory(issue, 'future_project')).length;

  return { criticalSafety, urgent, specialist, surpriseCost, negotiation, notTypical, futureProject };
};

const getSystemDecisionReason = (drivers: ReturnType<typeof getSystemDriverSummary>): string => {
  if (drivers.criticalSafety && drivers.specialist) return 'Safety + specialist';
  if (drivers.urgent && drivers.surpriseCost) return 'Urgent + surprise cost';
  if (drivers.negotiation && drivers.specialist) return 'Negotiation + specialist';
  if (drivers.negotiation) return 'Negotiation leverage';
  if (drivers.surpriseCost) return 'Surprise cost';
  if (drivers.urgent) return 'Verify before close';
  if (drivers.futureProject) return 'Ownership budget';
  return 'Review with buyer';
};

const getLeadSystemRows = (systems: SystemSummary[], issueMap: Map<number, InspectionIssue>) => systems
  .filter((system) => system.issueCount > 0)
  .map((system) => {
    const issues = getIssuesForSystem(system, issueMap);
    const drivers = getSystemDriverSummary(issues);
    const score = (
      drivers.criticalSafety * 10
      + drivers.urgent * 7
      + drivers.specialist * 5
      + drivers.surpriseCost * 5
      + drivers.negotiation * 4
      + drivers.notTypical * 3
      + Math.min(system.costMax / 2500, 8)
    );
    return { system, drivers, reason: getSystemDecisionReason(drivers), score };
  })
  .sort((a, b) => b.score - a.score || b.system.costMax - a.system.costMax)
  .slice(0, 3);

const buildSystemsWhyItMatters = (payload: InspectionReportPayload, leadSystems: Array<{ system: SystemSummary }>): string => {
  const driverLabels: string[] = [];
  const hasCriticalSafety = payload.issueList.some((issue) => issueHasCategory(issue, 'critical_safety'));
  const hasUrgent = payload.issueList.some((issue) => issueHasTag(issue, 'urgent_repair') || getTimeframeBucket(issue) === 'beforeClose' || getTimeframeBucket(issue) === 'nextMonth');
  const hasSpecialist = payload.issueList.some((issue) => issueHasTag(issue, 'specialist_required') || issue.specialist_evaluation_needed);
  const hasSurprise = payload.issueList.some((issue) => issueHasCategory(issue, 'surprise_cost'));
  const hasNegotiation = payload.issueList.some((issue) => issueHasCategory(issue, 'negotiation_leverage'));

  if (hasUrgent || hasCriticalSafety) driverLabels.push('urgent safety');
  if (hasSpecialist) driverLabels.push('specialist');
  if (hasSurprise) driverLabels.push('surprise-cost');
  if (!hasSurprise && hasNegotiation) driverLabels.push('negotiation');

  const systems = leadSystems.map((row) => row.system.label);
  if (!driverLabels.length || !systems.length) return 'System findings should be reviewed with the buyer before deciding next steps.';
  return `${driverLabels.join(', ')} findings are concentrated across ${formatInlineList(systems)}.`;
};

const buildSystemsDecisionReadout = (
  payload: InspectionReportPayload,
  systems: SystemSummary[],
  issueMap: Map<number, InspectionIssue>,
) => {
  const leadSystems = getLeadSystemRows(systems, issueMap);
  return {
    posture: getReportPosture(payload),
    whyItMatters: buildSystemsWhyItMatters(payload, leadSystems),
    actions: getSystemsDecisionActions(payload),
    leadSystems,
  };
};

function DecisionReadoutCard({
  readout,
  className = '',
}: {
  readout: ReturnType<typeof buildSystemsDecisionReadout>;
  className?: string;
}) {
  return (
    <Card className={`decision-readout-card ${className}`.trim()}>
      <IconTitle icon={ClipboardCheck} title="Decision Readout" />
      <div className="decision-posture">
        <span>Recommended posture</span>
        <strong>{readout.posture}</strong>
      </div>
      <div className="decision-reason">
        <span>Why it matters</span>
        <p>{readout.whyItMatters}</p>
      </div>
      <div className="decision-actions">
        {readout.actions.map((action) => (
          <span key={action}>{action}</span>
        ))}
      </div>
      <div className="decision-start-list">
        <span>Start here</span>
        {readout.leadSystems.map((row) => {
          const Icon = getSystemIcon(row.system.id);
          return (
            <div key={row.system.id} className={`decision-start-row system-${row.system.worstSeverity}`}>
              <span className="system-icon-badge">
                <Icon size={15} />
              </span>
              <div>
                <strong>{row.system.label}</strong>
                <em>{row.reason}</em>
              </div>
            </div>
          );
        })}
      </div>
      <p className="decision-guardrail">
        Use this to guide the buyer conversation and repair request. Do not diagnose final scope, code compliance, or repair method.
      </p>
    </Card>
  );
}

type SystemHealthTone = 'critical' | 'moderate' | 'minor' | 'clear';

const getSystemHealthTone = (system: SystemSummary): SystemHealthTone => {
  if (!system.issueCount) return 'clear';
  if (system.worstSeverity === 'critical') return 'critical';
  if (system.worstSeverity === 'moderate') return 'moderate';
  return 'minor';
};

const systemHealthRank: Record<SystemHealthTone, number> = {
  critical: 0,
  moderate: 1,
  minor: 2,
  clear: 3,
};

const sortSystemsForQuickView = (systems: SystemSummary[]) => [...systems].sort((a, b) => {
  const toneDelta = systemHealthRank[getSystemHealthTone(a)] - systemHealthRank[getSystemHealthTone(b)];
  if (toneDelta) return toneDelta;
  return b.costMax - a.costMax || b.issueCount - a.issueCount || a.label.localeCompare(b.label);
});

type SystemHealthLane = {
  tone: SystemHealthTone;
  label: string;
  systems: SystemSummary[];
};

const getSystemSpecialists = (issues: InspectionIssue[]): string[] => Array.from(new Set(issues
  .filter((issue) => issue.specialist_evaluation_needed || issueHasTag(issue, 'specialist_required'))
  .map((issue) => issue.specialist_type || 'Specialist')));

function SystemConditionMapCard({
  systems,
  selectedSystemId,
  onSelectSystem,
  className = '',
}: {
  systems: SystemSummary[];
  selectedSystemId: string;
  onSelectSystem: (systemId: string) => void;
  className?: string;
}) {
  const lanes: SystemHealthLane[] = [
    { tone: 'critical', label: 'Major', systems: sortSystemsForQuickView(systems.filter((system) => getSystemHealthTone(system) === 'critical')) },
    { tone: 'moderate', label: 'Needs attention', systems: sortSystemsForQuickView(systems.filter((system) => getSystemHealthTone(system) === 'moderate')) },
    { tone: 'minor', label: 'Monitor', systems: sortSystemsForQuickView(systems.filter((system) => getSystemHealthTone(system) === 'minor')) },
    { tone: 'clear', label: 'Clear', systems: sortSystemsForQuickView(systems.filter((system) => getSystemHealthTone(system) === 'clear')) },
  ];
  return (
    <Card className={`system-condition-map-card ${className}`.trim()}>
      <div className="system-condition-heading">
        <IconTitle icon={ClipboardCheck} title="System Condition Map" />
        <p>Select a system to view details.</p>
      </div>
      <div className="system-condition-lanes">
        {lanes.map((lane) => (
          <div key={lane.tone} className={`system-condition-lane system-${lane.tone}`}>
            <div className="system-condition-lane-head">
              <span>{lane.label}</span>
              <strong>{lane.systems.length}</strong>
            </div>
            <div className="system-condition-list">
              {lane.systems.length ? lane.systems.map((system) => (
                <SystemConditionTile
                  key={system.id}
                  system={system}
                  selected={system.id === selectedSystemId}
                  onSelect={() => onSelectSystem(system.id)}
                />
              )) : <span className="system-condition-empty">None</span>}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function SystemConditionTile({
  system,
  selected,
  onSelect,
}: {
  system: SystemSummary;
  selected: boolean;
  onSelect: () => void;
}) {
  const Icon = getSystemIcon(system.id);
  const tone = getSystemHealthTone(system);
  return (
    <button
      className={`system-health-tile system-${tone} ${selected ? 'is-active' : ''}`.trim()}
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
    >
      <span className="system-icon-badge">
        <Icon size={18} />
      </span>
      <div>
        <strong>{system.label}</strong>
        <em>{system.issueCount ? `${system.issueCount} ${system.issueCount === 1 ? 'issue' : 'issues'} · ${formatCompactCostRange(system.costMin, system.costMax)}` : 'No findings'}</em>
      </div>
      <ChevronRight className="system-selection-affordance" size={16} aria-hidden="true" />
    </button>
  );
}

function SelectedSystemPanel({
  system,
  issueMap,
  onNavigate,
}: {
  system: SystemSummary;
  issueMap: Map<number, InspectionIssue>;
  onNavigate: (page: Page, severity?: IssueSeverity, issueNumber?: number) => void;
}) {
  const Icon = getSystemIcon(system.id);
  const tone = getSystemHealthTone(system);
  const issues = getIssuesForSystem(system, issueMap);
  const timeframe = getSystemTimeframeLabel(issues);
  const specialists = getSystemSpecialists(issues);
  const systemTimelineGroups = getTimeframeCostRows(issues);
  const statusLabel = !system.issueCount
    ? 'Clear'
    : tone === 'critical'
      ? 'Major issues'
      : tone === 'moderate'
        ? 'Needs attention'
        : 'Monitor';
  const specialistLabel = specialists.length ? formatInlineList(specialists.slice(0, 3)) : 'Not flagged';

  return (
    <Card className={`selected-system-card system-${tone}`}>
      <div className="card-title-split">
        <IconTitle icon={Icon} title={system.label} />
        <Badge tone={system.issueCount ? severityTone(system.worstSeverity) : 'success'}>{statusLabel}</Badge>
      </div>
      <p className="selected-system-descriptor">{getSystemDescriptor(system.id)}</p>

      <div className="selected-system-metrics">
        <div>
          <span>Issues</span>
          <strong>{system.issueCount || 'None'}</strong>
        </div>
        <div>
          <span>Cost range</span>
          <strong>{system.issueCount ? formatCompactCostRange(system.costMin, system.costMax) : '$0'}</strong>
        </div>
        <div>
          <span>Timing</span>
          <strong>{timeframe}</strong>
        </div>
        <div>
          <span>Specialist</span>
          <strong>{specialistLabel}</strong>
        </div>
      </div>

      <p className="selected-system-summary">{system.summary}</p>

      {!!issues.length && (
        <div className="system-action-timeline">
          <div className="system-action-timeline-head">
            <strong>System action timeline</strong>
            <span>All {issues.length} {issues.length === 1 ? 'issue' : 'issues'}</span>
          </div>
          <div className="system-action-timeline-groups">
            {systemTimelineGroups.map((group) => (
              <section key={group.id} className={`system-action-timeline-group timeline-${group.accentClass}`}>
                <div className="system-action-timeline-group-head">
                  <div>
                    <strong>{group.label}</strong>
                    <span>{group.issues.length} {group.issues.length === 1 ? 'issue' : 'issues'}</span>
                  </div>
                  <span>{formatCompactCostRange(group.min, group.max)} estimated</span>
                </div>
                <div className="selected-system-driver-list">
                  {getIssuesSortedByPriority(group.issues).map((issue) => (
                    <button
                      key={issue.issueNumber}
                      className={`selected-system-driver issue-${normalizeSeverity(issue)}`}
                      type="button"
                      onClick={() => {
                        captureAnalyticsEvent('system_issue_opened', {
                          system_category: system.id,
                          severity: normalizeSeverity(issue),
                        });
                        onNavigate('issues', 'all', issue.issueNumber);
                      }}
                      aria-label={`View issue ${issue.issueNumber}: ${issue.title}`}
                    >
                      <span className={`issue-dot issue-dot-${normalizeSeverity(issue)}`} />
                      <div>
                        <strong>#{issue.issueNumber} {issue.title}</strong>
                        <span className="selected-system-driver-meta">
                          <span>{issue.location || formatSystemLabel(issue.systemCategory || system.id)}</span>
                          <span>{formatCompactCostRange(issueCostMin(issue), issueCostMax(issue))}</span>
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

const getSystemTimeframeLabel = (issues: InspectionIssue[]): string => {
  if (!issues.length) return 'No action needed';
  const bucket = issues.reduce<TimeframeBucketId>((earliest, issue) => {
    const next = getTimeframeBucket(issue);
    return systemTimeframeRank[next] < systemTimeframeRank[earliest] ? next : earliest;
  }, 'later');
  return systemTimeframeLabels[bucket];
};

const buildSystemsClientScript = (
  criticalSystems: SystemSummary[],
  attentionSystems: SystemSummary[],
  okSystems: SystemSummary[],
): string => {
  const firstCritical = criticalSystems[0];
  const topAttention = attentionSystems.slice(0, 2).map((system) => system.label);
  const clearExamples = okSystems.slice(0, 3).map((system) => system.label);
  if (firstCritical) {
    return `"${firstCritical.label} is the first system to focus on before closing. ${topAttention.length ? `${topAttention.join(' and ')} should be planned around next.` : 'The remaining items are lower priority.'} Good news: ${clearExamples.length ? `${clearExamples.join(', ')} had no reported issues.` : 'several inspected systems had no reported issues.'}"`;
  }
  if (topAttention.length) {
    return `"There are no critical systems, but ${topAttention.join(' and ')} should be planned into the first ownership window. ${clearExamples.length ? `${clearExamples.join(', ')} had no reported issues.` : 'Several systems came through without reportable findings.'}"`;
  }
  return `"The inspected systems are largely clear in this report. ${clearExamples.length ? `${clearExamples.join(', ')} had no reported issues.` : 'No major system findings are called out.'}"`;
};

function SystemStatusRow({ system }: { system: SystemSummary }) {
  const Icon = getSystemIcon(system.id);
  return (
    <div className={`system-status-row system-${system.worstSeverity}`}>
      <span className="system-icon-badge">
        <Icon size={16} />
      </span>
      <div>
        <strong>{system.label}</strong>
        <span>{system.issueCount} {system.issueCount === 1 ? 'issue' : 'issues'} · {severityLabel(system.worstSeverity)}</span>
      </div>
      <ChevronRight size={16} />
    </div>
  );
}

function SystemGroup({
  title,
  tone,
  systems,
  issueMap,
}: {
  title: string;
  tone: 'critical' | 'moderate' | 'minor';
  systems: SystemSummary[];
  issueMap: Map<number, InspectionIssue>;
}) {
  return (
    <section className={`system-priority-group system-${tone}`}>
      <div className={`systems-group-heading systems-group-${tone}`}>
        <span />
        <strong>{title}</strong>
        {tone === 'minor' && <em>· {systems.length} systems</em>}
      </div>
      <div className={`system-priority-grid system-priority-grid-${tone}`}>
        {systems.map((system) => (
          <SystemDetailCard key={system.id} system={system} issueMap={issueMap} compact={tone === 'minor'} />
        ))}
      </div>
    </section>
  );
}

function SystemDetailCard({
  system,
  issueMap,
  compact = false,
}: {
  system: SystemSummary;
  issueMap: Map<number, InspectionIssue>;
  compact?: boolean;
}) {
  const Icon = getSystemIcon(system.id);
  const issues = getIssuesForSystem(system, issueMap);
  const timeframe = getSystemTimeframeLabel(issues);
  return (
    <article id={`system-detail-${system.id}`} className={`system-detail-card system-${system.issueCount ? system.worstSeverity : 'clear'} ${compact ? 'compact' : ''}`}>
      <div className="system-card-head">
        <div className="system-card-title">
          <span className="system-icon-badge">
            <Icon size={compact ? 16 : 18} />
          </span>
          <div>
            <h3>{system.label}</h3>
            <p>{getSystemDescriptor(system.id)}</p>
          </div>
        </div>
        {system.issueCount ? <Badge tone={severityTone(system.worstSeverity)}>{system.worstSeverity === 'critical' ? 'Critical' : 'Attention'}</Badge> : <ChevronRight size={16} />}
      </div>
      <p className="system-card-copy">{system.summary}</p>
      {!!system.issueCount && (
        <div className="system-card-footer">
          <div>
            <span>{system.issueCount} {system.issueCount === 1 ? 'issue' : 'issues'}</span>
            <strong>{formatCompactCostRange(system.costMin, system.costMax)}</strong>
          </div>
          <span>{timeframe}</span>
        </div>
      )}
    </article>
  );
}

function MobileSystemPanel({
  system,
  issueMap,
  open,
  priority = false,
  onToggle,
}: {
  system: SystemSummary;
  issueMap: Map<number, InspectionIssue>;
  open: boolean;
  priority?: boolean;
  onToggle: () => void;
}) {
  const Icon = getSystemIcon(system.id);
  const issues = getIssuesForSystem(system, issueMap);
  const timeframe = getSystemTimeframeLabel(issues);
  return (
    <section id={`mobile-system-detail-${system.id}`} className={`mobile-system-panel system-${system.issueCount ? system.worstSeverity : 'clear'} ${priority ? 'priority' : ''}`}>
      <button className="mobile-system-toggle" type="button" onClick={onToggle}>
        <div>
          {priority && <Badge tone="critical">Priority</Badge>}
          <span className="system-icon-badge">
            <Icon size={18} />
          </span>
          <div>
            <strong>{system.label}</strong>
            <span>{system.issueCount ? `${timeframe} · ${system.issueCount} ${system.issueCount === 1 ? 'issue' : 'issues'}` : 'No reported issues'}</span>
          </div>
        </div>
        <div>
          {!!system.issueCount && <strong>{formatCompactCurrency(system.costAvg)}</strong>}
          <ChevronDown className={open ? 'open' : ''} size={18} />
        </div>
      </button>
      {open && (
        <div className="mobile-system-details">
          {issues.length ? (
            <>
              <div className="system-detail-issues">
                {issues.slice(0, 4).map((issue) => (
                  <div key={issue.issueNumber}>
                    <span className={`issue-dot issue-dot-${normalizeSeverity(issue)}`} />
                    <div>
                      <strong>{issue.title}</strong>
                      <p>{issue.location || issue.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mobile-system-cost">
                <span>Cost range</span>
                <strong>{formatCompactCostRange(system.costMin, system.costMax)}</strong>
              </div>
            </>
          ) : (
            <p>{system.summary}</p>
          )}
        </div>
      )}
    </section>
  );
}

function IssuesPage({
  payload,
  workspace,
  setWorkspace,
  onNavigate,
}: {
  payload: InspectionReportPayload;
  workspace: WorkspaceState;
  setWorkspace: React.Dispatch<React.SetStateAction<WorkspaceState>>;
  onNavigate: (page: Page, severity?: IssueSeverity, issueNumber?: number) => void;
}) {
  const [query, setQuery] = useState('');
  const [severity, setSeverity] = useState<IssueSeverity>(getIssueSeverityFromSearch);
  const [system, setSystem] = useState('all');
  const [timing, setTiming] = useState<'all' | TimeframeBucketId>('all');
  const [sortMode, setSortMode] = useState<IssueSortMode>('priority');
  const [viewMode, setViewMode] = useState<IssueViewMode>('cards');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [focusedIssueNumber, setFocusedIssueNumber] = useState<number | null>(getIssueNumberFromSearch);
  const selected = new Set(workspace.negotiation_builder.selectedIssueNumbers);
  const allIssues = getIssuesSortedByPriority(payload.issueList);
  const systems = [...new Set(payload.issueList.map((issue) => issue.systemCategory || 'miscellaneousSystems'))]
    .sort((a, b) => formatSystemLabel(a).localeCompare(formatSystemLabel(b)));
  const selectedIssues = allIssues.filter((issue) => selected.has(issue.issueNumber));
  const selectedTotals = costTotalsForIssues(selectedIssues);
  const selectedCostLabel = selectedIssues.length ? formatCompactCostRange(selectedTotals.min, selectedTotals.max) : '$0';
  const queryText = query.trim().toLowerCase();

  const sortLabels: Record<IssueSortMode, string> = {
    priority: 'priority',
    cost: 'cost',
    system: 'system',
    timeframe: 'timing',
  };

  const activeFilterLabels = [
    focusedIssueNumber ? `Issue #${focusedIssueNumber}` : null,
    queryText ? 'Search' : null,
    severity !== 'all' ? severityLabel(severity) : null,
    system !== 'all' ? formatSystemLabel(system) : null,
    timing !== 'all' ? timeframeBuckets[timing].label : null,
  ].filter(Boolean) as string[];
  const hasAppliedFilters = activeFilterLabels.length > 0 || sortMode !== 'priority';
  const appliedFilterCount = activeFilterLabels.length + (sortMode !== 'priority' ? 1 : 0);

  useEffect(() => {
    const reachedDepths = new Set<number>();
    const onScroll = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      const depth = (window.scrollY / scrollable) * 100;
      ([25, 50, 75, 100] as const).forEach((threshold) => {
        if (depth >= threshold && !reachedDepths.has(threshold)) {
          reachedDepths.add(threshold);
          captureAnalyticsEvent('issue_list_scrolled', { depth_bucket: threshold });
        }
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const filtered = sortIssuesForMode(allIssues.filter((issue) => {
    const matchesFocusedIssue = !focusedIssueNumber || issue.issueNumber === focusedIssueNumber;
    const matchesSearch = !queryText || getIssueSearchText(issue).includes(queryText);
    const matchesSeverity = severity === 'all' || normalizeSeverity(issue) === severity;
    const matchesSystem = system === 'all' || (issue.systemCategory || 'miscellaneousSystems') === system;
    const matchesTiming = timing === 'all' || getTimeframeBucket(issue) === timing;
    return matchesFocusedIssue && matchesSearch && matchesSeverity && matchesSystem && matchesTiming;
  }), sortMode);

  const toggleIssue = (issueNumber: number) => {
    const issue = allIssues.find((candidate) => candidate.issueNumber === issueNumber);
    const action = selected.has(issueNumber) ? 'removed' : 'added';
    if (issue) {
      captureAnalyticsEvent('issue_draft_toggled', {
        action,
        severity: normalizeSeverity(issue),
        system_category: issue.systemCategory || 'miscellaneousSystems',
        selection_source: 'issues',
      });
    }
    setWorkspace((current) => {
      const next = new Set(current.negotiation_builder.selectedIssueNumbers);
      if (next.has(issueNumber)) next.delete(issueNumber);
      else next.add(issueNumber);
      return {
        ...current,
        negotiation_builder: {
          ...current.negotiation_builder,
          selectedIssueNumbers: [...next],
        },
      };
    });
  };

  const clearSelected = () => {
    setWorkspace((current) => ({
      ...current,
      negotiation_builder: {
        ...current.negotiation_builder,
        selectedIssueNumbers: [],
      },
    }));
  };

  const clearFilters = () => {
    setQuery('');
    setSeverity('all');
    setSystem('all');
    setTiming('all');
    setSortMode('priority');
    setFiltersOpen(false);
    if (focusedIssueNumber) {
      setFocusedIssueNumber(null);
      onNavigate('issues');
    }
  };

  return (
    <div className="page-stack issues-page">
      <PageHeader title="All Issues" subtitle={`${payload.issueList.length} findings. Review issues and choose what to include in a BINSR draft.`} />

      {focusedIssueNumber && (
        <div className="focused-issue-notice" role="status">
          <span>Viewing issue #{focusedIssueNumber}</span>
          <button className="text-link" type="button" onClick={clearFilters}>Back to all issues <ChevronRight size={14} /></button>
        </div>
      )}

      <section className={filtersOpen ? 'issues-worklist-toolbar is-filter-open' : 'issues-worklist-toolbar'} aria-label="Issue list controls">
        <div className="issue-primary-controls">
          <label className="search-box">
            <Search size={16} />
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search issues, systems, locations" />
          </label>
          <button
            className={filtersOpen || appliedFilterCount ? 'issue-filter-trigger active' : 'issue-filter-trigger'}
            type="button"
            onClick={() => setFiltersOpen((current) => !current)}
            aria-expanded={filtersOpen}
          >
            <SlidersHorizontal size={14} />
            Filters & sort
            {appliedFilterCount > 0 && <strong>{appliedFilterCount}</strong>}
          </button>
          <div className="issue-view-toggle" aria-label="Issue view">
            <button className={viewMode === 'cards' ? 'active' : ''} type="button" onClick={() => {
              captureAnalyticsEvent('issue_view_changed', { view_mode: 'cards' });
              setViewMode('cards');
            }}>
              <ListChecks size={14} />
              Cards
            </button>
            <button className={viewMode === 'table' ? 'active' : ''} type="button" onClick={() => {
              captureAnalyticsEvent('issue_view_changed', { view_mode: 'table' });
              setViewMode('table');
            }}>
              <Table2 size={14} />
              Table
            </button>
          </div>
          {hasAppliedFilters && <button className="text-link issue-clear-action" type="button" onClick={clearFilters}>Clear all</button>}
        </div>
        <div className="issue-toolbar-context">
          <p className="issue-result-count">
            {hasAppliedFilters ? <><strong>{filtered.length}</strong> of {allIssues.length} findings</> : <>{allIssues.length} findings, ordered by {sortLabels[sortMode]}</>}
          </p>
        </div>
        {filtersOpen && (
          <div className="issue-filter-panel">
            <div className="issue-filter-panel-body">
              <div className="issue-filter-panel-head">
                <strong>Refine issues</strong>
                <span>Filters update the list immediately.</span>
              </div>
              <div className="issue-filter-fields">
                <label className="issue-select-field">
                  <span>System</span>
                  <select value={system} onChange={(event) => {
                    captureAnalyticsEvent('issue_filter_changed', { filter_name: 'system', filter_value: event.target.value });
                    setSystem(event.target.value);
                  }} aria-label="Filter by system">
                    <option value="all">All systems</option>
                    {systems.map((systemId) => (
                      <option key={systemId} value={systemId}>{formatSystemLabel(systemId)}</option>
                    ))}
                  </select>
                  <ChevronDown size={15} aria-hidden="true" />
                </label>
                <label className="issue-select-field">
                  <span>Timing</span>
                  <select value={timing} onChange={(event) => {
                    const nextTiming = event.target.value as 'all' | TimeframeBucketId;
                    captureAnalyticsEvent('issue_filter_changed', { filter_name: 'timing', filter_value: nextTiming });
                    setTiming(nextTiming);
                  }} aria-label="Filter by timing">
                    <option value="all">All timing</option>
                    {(Object.keys(timeframeBuckets) as TimeframeBucketId[]).map((bucket) => (
                      <option key={bucket} value={bucket}>{timeframeBuckets[bucket].label}</option>
                    ))}
                  </select>
                  <ChevronDown size={15} aria-hidden="true" />
                </label>
                <label className="issue-select-field">
                  <span>Sort by</span>
                  <select value={sortMode} onChange={(event) => {
                    const nextSortMode = event.target.value as IssueSortMode;
                    captureAnalyticsEvent('issue_sort_changed', { sort_mode: nextSortMode });
                    setSortMode(nextSortMode);
                  }} aria-label="Sort issues">
                    <option value="priority">Priority</option>
                    <option value="cost">Cost</option>
                    <option value="system">System</option>
                    <option value="timeframe">Timing</option>
                  </select>
                  <ChevronDown size={15} aria-hidden="true" />
                </label>
              </div>
              <div className="issue-severity-filter">
                <span>Severity</span>
                <div className="filter-pills">
                  {(['all', 'critical', 'moderate', 'minor'] as const).map((value) => (
                    <button key={value} className={severity === value ? 'pill active' : 'pill'} type="button" onClick={() => {
                      captureAnalyticsEvent('issue_filter_changed', { filter_name: 'severity', filter_value: value });
                      setSeverity(value);
                    }}>
                      {value === 'all' ? 'All severities' : severityLabel(value)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </section>

      {selectedIssues.length > 0 && (
        <aside className="selected-worklist-summary" aria-label="BINSR draft selection">
          <div className="selected-worklist-copy">
            <span>BINSR draft</span>
            <strong>{selectedIssues.length} {selectedIssues.length === 1 ? 'finding' : 'findings'} included</strong>
            <p>Nothing has been sent. Review and refine this draft with your buyer.</p>
          </div>
          <div className="selected-worklist-exposure">
            <span>Est. repair range</span>
            <strong>{selectedCostLabel}</strong>
          </div>
          <div className="selected-worklist-actions">
            <button className="selected-worklist-primary" type="button" onClick={() => {
              captureAnalyticsEvent('draft_review_prompt_opened', { selected_issue_count: selectedIssues.length });
              onNavigate('negotiation');
            }}>Review BINSR draft <ChevronRight size={14} /></button>
            <button className="text-link" type="button" onClick={clearSelected}>Clear draft</button>
          </div>
        </aside>
      )}

      {!allIssues.length ? (
        <Card className="empty-state-card">
          <IconTitle icon={FileText} title="No inspection issues were extracted" />
          <p>Once the transformed report includes findings, they will appear here for review.</p>
        </Card>
      ) : !filtered.length ? (
        <Card className="empty-state-card">
          <IconTitle icon={Search} title="No issues match these filters" />
          <p>Clear filters or broaden the search to return to the full issue list.</p>
          <button className="secondary-action compact-action" type="button" onClick={clearFilters}>Clear filters</button>
        </Card>
      ) : (
        viewMode === 'table'
          ? <IssueTable issues={filtered} selected={selected} onToggle={toggleIssue} />
          : <IssueList issues={filtered} selected={selected} onToggle={toggleIssue} />
      )}
    </div>
  );
}

const compactBinsrActionText = (value: string): string =>
  value
    .replace(/\s+/g, ' ')
    .replace(/\b(recommend|recommended|recommendation)\b[:\s-]*/i, '')
    .replace(/\.$/, '')
    .trim();

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const getBinsrActionText = (issue: InspectionIssue, supportText?: string): string => {
  const support = compactBinsrActionText(
    supportText?.trim()
    || issue.fileRecco
    || issue.aiRecco
    || issue.repair_approach
    || issue.typicalBuyerAction
    || issue.title,
  );
  const itemPrefix = new RegExp(`^\\s*#?${issue.issueNumber}[\\s.:,-]*`, 'i');
  const location = (issue.location || issue.nspireLocation || '').trim();
  const locationPrefix = location
    ? new RegExp(`^\\s*#?${issue.issueNumber}[\\s.:,-]*${escapeRegExp(location)}\\s*:\\s*`, 'i')
    : null;
  return support.replace(locationPrefix || itemPrefix, '').trim();
};

const BINSR_WORKSHEET_TITLE = "Residential Buyer's Inspection Notice and Seller's Response";
const BINSR_WORKSHEET_SUBTITLE = 'BINSR item language worksheet';
const BINSR_PREVIEW_KICKER = 'Draft preparation copy';
const BINSR_PREVIEW_NOTE = 'This preview contains selected Items Disapproved wording only. Review and copy it into the official Arizona REALTORS BINSR in your licensed forms platform.';

const getBinsrContractorLabel = (issue: InspectionIssue): string => {
  const specialist = issue.specialist_type?.trim();
  if (specialist) return specialist.toLowerCase().includes('licensed') ? specialist : `licensed ${specialist}`;
  const system = formatSystemLabel(issue.systemCategory || 'miscellaneousSystems').toLowerCase();
  if (system.includes('electrical')) return 'licensed electrician';
  if (system.includes('plumbing')) return 'licensed plumber';
  if (system.includes('roof')) return 'licensed roofing contractor';
  if (system.includes('hvac')) return 'licensed HVAC contractor';
  return 'qualified contractor';
};

const getBinsrCompactClause = (issue: InspectionIssue, supportText?: string): string => {
  const support = getBinsrActionText(issue, supportText);
  const location = issue.location || issue.nspireLocation;
  const isActionable = /\b(address|correct|evaluate|have|install|obtain|remove|repair|replace|reset|seal|service)\b/i.test(support);
  const contractor = getBinsrContractorLabel(issue);
  const article = /^[aeiou]/i.test(contractor) ? 'an' : 'a';
  const action = isActionable
    ? support
    : `Have ${article} ${contractor} evaluate and correct ${issue.title.toLowerCase()}, including related conditions as applicable`;
  return `${issue.issueNumber} ${location ? `${location}: ` : ''}${action}.`;
};

const groupIssuesForBinsrPrint = (issues: InspectionIssue[]): Array<{ systemId: string; label: string; issues: InspectionIssue[] }> => {
  const groups = new Map<string, InspectionIssue[]>();
  issues.forEach((issue) => {
    const systemId = issue.systemCategory || 'miscellaneousSystems';
    groups.set(systemId, [...(groups.get(systemId) || []), issue]);
  });
  return [...groups.entries()]
    .map(([systemId, groupIssues]) => ({
      systemId,
      label: formatSystemLabel(systemId),
      issues: getIssuesSortedByPriority(groupIssues),
    }))
    .sort((a, b) => {
      const priorityDelta = (b.issues[0]?.issueNumber || 0) - (a.issues[0]?.issueNumber || 0);
      return a.label.localeCompare(b.label) || priorityDelta;
    });
};

function NegotiationPage({
  payload,
  workspace,
  setWorkspace,
  apiBase,
  routeToken,
}: {
  payload: InspectionReportPayload;
  workspace: WorkspaceState;
  setWorkspace: React.Dispatch<React.SetStateAction<WorkspaceState>>;
  apiBase?: string;
  routeToken?: string;
}) {
  const [activeMobileTab, setActiveMobileTab] = useState<'add' | 'strategy'>('add');
  const [generationState, setGenerationState] = useState<'idle' | 'generating' | 'error'>('idle');
  const [generationError, setGenerationError] = useState('');
  const [createdDraft, setCreatedDraft] = useState<GeneratedDocument | null>(null);
  const [printDocument, setPrintDocument] = useState<GeneratedDocument | null>(null);
  const [isDraftReviewOpen, setIsDraftReviewOpen] = useState(false);
  const [isDraftSummaryUpdating, setIsDraftSummaryUpdating] = useState(false);
  const [copiedDocumentId, setCopiedDocumentId] = useState<string | null>(null);
  const [editingBinsrIssueNumber, setEditingBinsrIssueNumber] = useState<number | null>(null);
  const [editingBinsrWording, setEditingBinsrWording] = useState('');
  const draftSummaryTimerRef = useRef<number | null>(null);
  const totals = getNegotiationTotals(payload, workspace);
  const selectedNumbers = new Set(workspace.negotiation_builder.selectedIssueNumbers);
  const selectedIssueCount = workspace.negotiation_builder.selectedIssueNumbers.length;
  const allCandidateIssues = getIssuesSortedByPriority(payload.issueList);
  const criticalIssueNumbers = allCandidateIssues
    .filter((issue) => normalizeSeverity(issue) === 'critical')
    .map((issue) => issue.issueNumber);
  const criticalAvailableCount = criticalIssueNumbers.filter((issueNumber) => !selectedNumbers.has(issueNumber)).length;
  const candidateTimelineGroups = (Object.keys(timeframeBuckets) as TimeframeBucketId[]).map((id) => {
    const issues = allCandidateIssues.filter((issue) => getTimeframeBucket(issue) === id);
    return {
      id,
      ...timeframeBuckets[id],
      issues,
      criticalCount: issues.filter((issue) => normalizeSeverity(issue) === 'critical').length,
      description: id === 'beforeClose'
        ? 'Review for the inspection response period.'
        : id === 'nextMonth'
          ? 'Plan for the first 30 days of ownership.'
          : id === 'afterMoveIn'
            ? 'Budget after moving in, within about 90 days.'
            : 'Monitor or plan as a future project.',
    };
  }).filter((group) => group.issues.length > 0);
  const selectedRepairMin = totals.selected.reduce((sum, issue) => sum + issueCostMin(issue), 0);
  const selectedRepairMax = totals.selected.reduce((sum, issue) => sum + issueCostMax(issue), 0);
  const generatedRequestDocuments = workspace.generated_documents.filter((document) => (
    document.type === 'repairs' || document.type === 'credits' || document.type === 'lump_sum'
  ));
  const firstCandidateTimelineId = candidateTimelineGroups.find((group) => group.issues.length)?.id;
  const canPersistDraftCopy = Boolean(apiBase && routeToken);

  useEffect(() => () => {
    if (draftSummaryTimerRef.current !== null) window.clearTimeout(draftSummaryTimerRef.current);
  }, []);

  useEffect(() => {
    captureAnalyticsEvent('binsr_draft_viewed', { selected_issue_count: selectedIssueCount });
  }, []);

  const highlightDraftSummary = () => {
    if (draftSummaryTimerRef.current !== null) window.clearTimeout(draftSummaryTimerRef.current);
    setIsDraftSummaryUpdating(false);
    window.requestAnimationFrame(() => {
      setIsDraftSummaryUpdating(true);
      draftSummaryTimerRef.current = window.setTimeout(() => setIsDraftSummaryUpdating(false), 900);
    });
  };

  const toggleIssue = (issueNumber: number) => {
    const isIncluding = !selectedNumbers.has(issueNumber);
    const issue = payload.issueList.find((candidate) => candidate.issueNumber === issueNumber);
    if (issue) {
      captureAnalyticsEvent('binsr_source_issue_toggled', {
        action: isIncluding ? 'added' : 'removed',
        severity: normalizeSeverity(issue),
        system_category: issue.systemCategory || 'miscellaneousSystems',
      });
    }
    if (selectedNumbers.has(issueNumber) && editingBinsrIssueNumber === issueNumber) {
      setEditingBinsrIssueNumber(null);
      setEditingBinsrWording('');
    }
    setCreatedDraft(null);
    setWorkspace((current) => {
      const next = new Set(current.negotiation_builder.selectedIssueNumbers);
      if (next.has(issueNumber)) next.delete(issueNumber);
      else next.add(issueNumber);
      return {
        ...current,
        negotiation_builder: { ...current.negotiation_builder, selectedIssueNumbers: [...next] },
      };
    });
    if (isIncluding) highlightDraftSummary();
  };

  const includeCriticalFindings = () => {
    if (criticalAvailableCount) highlightDraftSummary();
    setCreatedDraft(null);
    setWorkspace((current) => {
      const next = new Set(current.negotiation_builder.selectedIssueNumbers);
      criticalIssueNumbers.forEach((issueNumber) => next.add(issueNumber));
      return {
        ...current,
        negotiation_builder: {
          ...current.negotiation_builder,
          selectedIssueNumbers: [...next],
        },
      };
    });
    setActiveMobileTab('strategy');
  };

  const clearPackage = () => {
    setCreatedDraft(null);
    setWorkspace((current) => ({
      ...current,
      negotiation_builder: {
        ...current.negotiation_builder,
        selectedIssueNumbers: [],
        priorityOverrides: {},
        costOverrides: {},
        justificationText: {},
        privateAgentNotes: {},
      },
    }));
    setActiveMobileTab('add');
  };

  const setJustification = (issueNumber: number, value: string) => {
    setCreatedDraft(null);
    setWorkspace((current) => ({
      ...current,
      negotiation_builder: {
        ...current.negotiation_builder,
        justificationText: {
          ...current.negotiation_builder.justificationText,
          [String(issueNumber)]: value,
        },
      },
    }));
  };

  const startBinsrWordingEdit = (issue: InspectionIssue) => {
    setEditingBinsrIssueNumber(issue.issueNumber);
    setEditingBinsrWording(
      getBinsrActionText(
        issue,
        workspace.negotiation_builder.justificationText[String(issue.issueNumber)],
      ),
    );
  };

  const saveBinsrWordingEdit = () => {
    if (editingBinsrIssueNumber == null) return;
    const issue = payload.issueList.find((candidate) => candidate.issueNumber === editingBinsrIssueNumber);
    setJustification(
      editingBinsrIssueNumber,
      issue ? getBinsrActionText(issue, editingBinsrWording) : compactBinsrActionText(editingBinsrWording),
    );
    setEditingBinsrIssueNumber(null);
    setEditingBinsrWording('');
  };

  const cancelBinsrWordingEdit = () => {
    setEditingBinsrIssueNumber(null);
    setEditingBinsrWording('');
  };

  const addGeneratedDocument = (document: GeneratedDocument) => {
    setWorkspace((current) => ({
      ...current,
      generated_documents: [
        normalizeGeneratedDocument(document),
        ...current.generated_documents.filter((existing) => existing.id !== document.id),
      ],
    }));
  };

  const buildBinsrWorksheetText = (issues: InspectionIssue[] = totals.selected): string => {
    const groups = groupIssuesForBinsrPrint(issues);
    const items = groups.flatMap((group) => [
      `${group.label}:`,
      ...group.issues.map((issue) => (
        getBinsrCompactClause(
          issue,
          workspace.negotiation_builder.justificationText[String(issue.issueNumber)],
        )
      )),
      '',
    ]);

    return [
      BINSR_PREVIEW_KICKER,
      BINSR_WORKSHEET_TITLE,
      BINSR_WORKSHEET_SUBTITLE,
      '',
      'PREMISES ADDRESS',
      payload.summary.address || '[Premises address]',
      '',
      'ITEMS DISAPPROVED',
      BINSR_PREVIEW_NOTE,
      '',
      ...items,
    ].join('\n').trim();
  };

  const buildLocalGeneratedDocument = (): GeneratedDocument => {
    return {
      id: `local-${Date.now()}`,
      type: 'repairs',
      title: 'BINSR wording copy',
      body: buildBinsrWorksheetText(),
      issueNumbers: totals.selected.map((issue) => issue.issueNumber),
      createdAt: new Date().toISOString(),
    };
  };

  const generatePacket = async () => {
    if (!totals.selected.length || generationState === 'generating') return;
    setGenerationState('generating');
    setGenerationError('');

    try {
      const created = buildLocalGeneratedDocument();
      if (canPersistDraftCopy) {
        const base = apiBase!.replace(/\/$/, '');
        const nextDocuments = [
          normalizeGeneratedDocument(created),
          ...workspace.generated_documents.filter((existing) => existing.id !== created.id),
        ];
        const stateResponse = await fetch(`${base}/update-report-state`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            token: routeToken,
            updates: [
              { path: 'workspace_state.negotiation_builder', value: workspace.negotiation_builder },
              { path: 'workspace_state.generated_documents', value: nextDocuments },
            ],
          }),
        });
        if (!stateResponse.ok) throw new Error('state_update_failed');
      }
      addGeneratedDocument(created);
      setCreatedDraft(created);
      setGenerationState('idle');
      captureAnalyticsEvent('draft_saved', { selected_issue_count: selectedIssueCount });
    } catch {
      const fallbackDocument = buildLocalGeneratedDocument();
      addGeneratedDocument(fallbackDocument);
      setCreatedDraft(fallbackDocument);
      setGenerationState('idle');
      setGenerationError(canPersistDraftCopy ? 'Saving to the report workspace failed, so a local wording copy was created.' : '');
    }
  };

  const copyDocumentText = async (document: GeneratedDocument) => {
    await navigator.clipboard?.writeText(document.body);
    setCopiedDocumentId(document.id);
    captureAnalyticsEvent('draft_copy_requested', { selected_issue_count: document.issueNumbers.length });
    setTimeout(() => setCopiedDocumentId(null), 1600);
  };

  const printWorksheetPdf = (document: GeneratedDocument) => {
    setPrintDocument(document);
    captureAnalyticsEvent('draft_print_requested', { selected_issue_count: document.issueNumbers.length });
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => window.print());
    });
  };

  const SaveDraftAction = ({ compact = false, top = false }: { compact?: boolean; top?: boolean }) => (
    <button
      className={top ? 'binsr-generate-action' : `draft-package-review-card generate-packet-action ${compact ? 'compact' : ''}`}
      type="button"
      onClick={generatePacket}
      disabled={!totals.selected.length || generationState === 'generating'}
    >
      {generationState === 'generating' ? <Loader2 className="spin-icon" size={16} /> : <PackageCheck size={16} />}
      <span>
        <strong>{generationState === 'generating' ? 'Saving a copy of this BINSR wording' : 'Save a copy of this BINSR wording'}</strong>
        {!top && <em>{totals.selected.length ? `${selectedIssueCount} selected. Save a reusable copy of the current worksheet text.` : 'Include a finding to begin.'}</em>}
      </span>
    </button>
  );

  return (
    <div className="page-stack negotiation-page">
      <PageHeader title="BINSR Draft" subtitle="Select report-supported findings, then review the items-disapproved wording with your buyer. Clear Casa does not complete or send the official BINSR." />

      <div className="negotiation-mobile-tabs" role="tablist" aria-label="BINSR draft views">
        <button className={activeMobileTab === 'add' ? 'active' : ''} type="button" onClick={() => {
          captureAnalyticsEvent('binsr_source_panel_opened', { panel: 'findings' });
          setActiveMobileTab('add');
        }}>
          Findings <span>{allCandidateIssues.length}</span>
        </button>
        <button className={activeMobileTab === 'strategy' ? 'active' : ''} type="button" onClick={() => {
          captureAnalyticsEvent('binsr_source_panel_opened', { panel: 'draft' });
          setActiveMobileTab('strategy');
        }}>
          Draft <span>{selectedIssueCount}</span>
        </button>
      </div>

      <div className="negotiation-workbench">
        <Card className={`negotiation-workbench-card add-panel ${activeMobileTab === 'add' ? 'is-active' : ''}`}>
          <div className="negotiation-card-head findings-card-head">
            <div>
              <h2>Findings for BINSR</h2>
              <p>Select only the report findings the buyer may disapprove. Included findings appear in the worksheet.</p>
            </div>
            <button className="findings-critical-action" type="button" onClick={includeCriticalFindings} disabled={!criticalAvailableCount}>
              <Sparkles size={15} />
              {criticalAvailableCount ? `Include ${criticalAvailableCount} critical` : 'Critical findings included'}
            </button>
          </div>

          {candidateTimelineGroups.map((group) => (
            <NegotiationCandidateGroup
              key={group.id}
              title={group.label}
              description={group.description}
              tone={group.accentClass}
              count={group.issues.length}
              collapsible
              defaultOpen={group.id === firstCandidateTimelineId}
              maxVisible={5}
              onToggleOpen={(isOpen) => {
                if (isOpen) captureAnalyticsEvent('binsr_timeline_expanded', { timeline_bucket: group.id });
              }}
            >
              {group.issues.map((issue) => (
              <NegotiationCandidateRow key={issue.issueNumber} issue={issue} selected={selectedNumbers.has(issue.issueNumber)} onToggle={() => toggleIssue(issue.issueNumber)} />
              ))}
            </NegotiationCandidateGroup>
          ))}

          {!allCandidateIssues.length && (
            <div className="negotiation-empty-state">
              <strong>No findings in this report</strong>
              <p>There are no report findings to add to the BINSR draft.</p>
            </div>
          )}
        </Card>

        <Card className={`negotiation-workbench-card draft-panel ${activeMobileTab === 'strategy' ? 'is-active' : ''}`}>
          <div className="negotiation-card-head">
            <div>
              <h2>Live BINSR draft</h2>
              <p>{isDraftReviewOpen ? 'Draft only. Included findings appear as Items Disapproved below, where wording can be edited directly.' : 'Review the selected Items Disapproved wording when you are ready.'}</p>
            </div>
          </div>
          <div className="binsr-draft-context">
            <div className="binsr-draft-context-summary-row">
              <div className={`binsr-draft-summary ${isDraftSummaryUpdating ? 'is-updating' : ''}`} aria-live="polite">
                <strong>{selectedIssueCount ? `${selectedIssueCount} items disapproved` : 'No items selected'}</strong>
                <span>{selectedIssueCount ? `Estimated repair range ${formatCostRange(selectedRepairMin, selectedRepairMax)}` : 'Include findings to begin the draft'}</span>
              </div>
              <button className="binsr-clear-button" type="button" onClick={clearPackage} disabled={!totals.selected.length} title="Remove every selected finding and its draft wording">
                Clear selections
              </button>
            </div>
            {isDraftReviewOpen ? (
              <>
                {createdDraft ? (
                  <div className="binsr-saved-draft-state" role="status">
                    <div>
                      <strong>Ready to transfer into your official BINSR</strong>
                    </div>
                    <div className="binsr-saved-draft-actions">
                      <button className="primary-button inline-action" type="button" onClick={() => copyDocumentText(createdDraft)}>
                        <Copy size={15} />
                        {copiedDocumentId === createdDraft.id ? 'Copied' : 'Copy wording'}
                      </button>
                      <button className="secondary-action compact-action" type="button" onClick={() => printWorksheetPdf(createdDraft)}>
                        <FileText size={15} />
                        Print or save preview PDF
                      </button>
                    </div>
                  </div>
                ) : (
                  <SaveDraftAction top />
                )}
                <button className="binsr-review-collapse" type="button" onClick={() => setIsDraftReviewOpen(false)}>
                  <ChevronUp size={15} />
                  Hide draft wording
                </button>
              </>
            ) : (
              <button className="binsr-review-draft-action" type="button" onClick={() => {
                captureAnalyticsEvent('draft_review_opened', { selected_issue_count: selectedIssueCount });
                setIsDraftReviewOpen(true);
              }} disabled={!selectedIssueCount}>
                <FileText size={16} />
                <span>
                  <strong>{selectedIssueCount ? 'Review draft wording' : 'Include findings to begin the draft'}</strong>
                  <em>{selectedIssueCount ? 'Open Items Disapproved to review and edit the current wording.' : 'Start with the priority findings in the left column.'}</em>
                </span>
                <ChevronDown size={16} aria-hidden="true" />
              </button>
            )}
          </div>

          {isDraftReviewOpen && (
            <BinsrPrintableDraft
              payload={payload}
              issues={totals.selected}
              workspace={workspace}
              embedded
              editingIssueNumber={editingBinsrIssueNumber}
              editingWording={editingBinsrWording}
              onEditingWordingChange={setEditingBinsrWording}
              onStartEditing={startBinsrWordingEdit}
              onSaveEditing={saveBinsrWordingEdit}
              onCancelEditing={cancelBinsrWordingEdit}
            />
          )}
        </Card>
      </div>

      <section className="binsr-print-section print-only-binsr ph-no-capture" aria-label="Printable BINSR draft">
        {printDocument ? (
          <BinsrWorksheetPrintSnapshot document={printDocument} />
        ) : (
          <BinsrPrintableDraft
            payload={payload}
            issues={totals.selected}
            workspace={workspace}
          />
        )}
      </section>

      <section className="generated-documents-section" aria-label="Saved BINSR wording copies">
        <div className="generated-documents-head">
          <div>
            <h2>Saved BINSR wording copies</h2>
            <p>Saved copies of the current Items Disapproved wording. Private notes stay out of the generated text.</p>
          </div>
          <span>{generatedRequestDocuments.length} saved</span>
        </div>
        {generationError && <div className="generation-status-note">{generationError}</div>}
        {generatedRequestDocuments.length ? (
          <div className="generated-document-list">
            {generatedRequestDocuments.map((document) => (
              <GeneratedDocumentCard
                key={document.id}
                document={document}
                onCopy={() => copyDocumentText(document)}
                onPrint={() => printWorksheetPdf(document)}
                copied={copiedDocumentId === document.id}
              />
            ))}
          </div>
        ) : (
          <div className="negotiation-empty-state generated-empty">
            <strong>No saved BINSR wording copies yet</strong>
            <p>Save a copy when the Items Disapproved wording is ready to transfer into the official BINSR.</p>
          </div>
        )}
      </section>

      {totals.selected.length > 0 && (
        <div className="negotiation-mobile-generate">
          <button className="primary-button inline-action" type="button" onClick={generatePacket} disabled={generationState === 'generating'}>
            {generationState === 'generating' ? <Loader2 className="spin-icon" size={16} /> : <PackageCheck size={16} />}
            {generationState === 'generating' ? 'Saving a copy of this BINSR wording' : 'Save a copy of this BINSR wording'}
          </button>
        </div>
      )}
    </div>
  );
}

function normalizeGeneratedDocument(document: GeneratedDocument): GeneratedDocument {
  return {
    ...document,
    body: document.body
      .replace('Draft support material for agent review. This is not an official legal form.', 'Support material for agent review. Contract terms, seller response, lender constraints, and licensed specialist input still control final wording.')
      .replace('Lump-Sum Request Summary', 'Combined Request Summary'),
  };
}

function formatGeneratedDocumentTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Just now';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

function BinsrPrintableDraft({
  payload,
  issues,
  workspace,
  embedded = false,
  editingIssueNumber = null,
  editingWording = '',
  onEditingWordingChange,
  onStartEditing,
  onSaveEditing,
  onCancelEditing,
}: {
  payload: InspectionReportPayload;
  issues: InspectionIssue[];
  workspace: WorkspaceState;
  embedded?: boolean;
  editingIssueNumber?: number | null;
  editingWording?: string;
  onEditingWordingChange?: (value: string) => void;
  onStartEditing?: (issue: InspectionIssue) => void;
  onSaveEditing?: () => void;
  onCancelEditing?: () => void;
}) {
  const groups = groupIssuesForBinsrPrint(issues);

  return (
    <div className={`binsr-print-shell ${embedded ? 'is-embedded' : ''}`}>
      <article className={`binsr-print-sheet ${embedded ? 'is-embedded' : ''}`}>
        <header className="binsr-print-header">
          <div>
            <span>{BINSR_PREVIEW_KICKER}</span>
            <h2>{BINSR_WORKSHEET_TITLE}</h2>
            <strong>{BINSR_WORKSHEET_SUBTITLE}</strong>
          </div>
        </header>

        <section className="binsr-print-fields">
          <label>
            <span>Premises address</span>
            <strong>{payload.summary.address || '________________________________________'}</strong>
          </label>
        </section>

        <section className="binsr-print-block binsr-items-block">
          <h3>Items disapproved</h3>
          <p className="binsr-print-preview-note">{BINSR_PREVIEW_NOTE}</p>
          {!issues.length ? (
            <p className="binsr-print-empty">No selected findings yet. Add findings to the BINSR draft before printing.</p>
          ) : (
            <div className="binsr-print-items">
              {groups.map((group) => (
                <div className="binsr-print-group" key={group.systemId}>
                  <strong>{group.label}:</strong>
                  {group.issues.map((issue) => {
                    const isEditing = embedded && editingIssueNumber === issue.issueNumber;
                    const wording = workspace.negotiation_builder.justificationText[String(issue.issueNumber)];
                    return (
                      <div className={`binsr-print-item-line ${isEditing ? 'is-editing' : ''}`} key={issue.issueNumber}>
                        {isEditing ? (
                          <>
                            <label htmlFor={`binsr-wording-${issue.issueNumber}`}>Draft item wording</label>
                            <textarea
                              id={`binsr-wording-${issue.issueNumber}`}
                              rows={3}
                              value={editingWording}
                              onChange={(event) => onEditingWordingChange?.(event.currentTarget.value)}
                              autoFocus
                            />
                            <div className="binsr-print-item-actions">
                              <button type="button" onClick={onCancelEditing}>Cancel</button>
                              <button className="is-primary" type="button" onClick={onSaveEditing}>Save wording</button>
                            </div>
                          </>
                        ) : (
                          <>
                            <p>{getBinsrCompactClause(issue, wording)}</p>
                            {embedded && (
                              <button className="binsr-print-edit-button" type="button" onClick={() => onStartEditing?.(issue)}>
                                Edit wording
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </section>
      </article>
    </div>
  );
}

function BinsrWorksheetPrintSnapshot({ document }: { document: GeneratedDocument }) {
  const lines = document.body.split('\n');
  const addressLabelIndex = lines.indexOf('PREMISES ADDRESS');
  const itemsLabelIndex = lines.indexOf('ITEMS DISAPPROVED');
  const premisesAddress = addressLabelIndex >= 0 ? lines[addressLabelIndex + 1]?.trim() || '[Premises address]' : '[Premises address]';
  const previewNote = itemsLabelIndex >= 0 ? lines[itemsLabelIndex + 1]?.trim() || BINSR_PREVIEW_NOTE : BINSR_PREVIEW_NOTE;
  const groups: Array<{ label: string; items: string[] }> = [];
  let activeGroup: { label: string; items: string[] } | null = null;

  lines.slice(itemsLabelIndex >= 0 ? itemsLabelIndex + 2 : 0).forEach((line) => {
    const value = line.trim();
    if (!value || value === previewNote) return;
    if (value.endsWith(':')) {
      activeGroup = { label: value, items: [] };
      groups.push(activeGroup);
      return;
    }
    if (activeGroup) activeGroup.items.push(value);
  });

  return (
    <div className="binsr-print-shell">
      <article className="binsr-print-sheet">
        <header className="binsr-print-header">
          <div>
            <span>{BINSR_PREVIEW_KICKER}</span>
            <h2>{BINSR_WORKSHEET_TITLE}</h2>
            <strong>{BINSR_WORKSHEET_SUBTITLE}</strong>
          </div>
        </header>

        <section className="binsr-print-fields">
          <label>
            <span>Premises address</span>
            <strong>{premisesAddress}</strong>
          </label>
        </section>

        <section className="binsr-print-block binsr-items-block">
          <h3>Items disapproved</h3>
          <p className="binsr-print-preview-note">{previewNote}</p>
          {groups.length ? (
            <div className="binsr-print-items">
              {groups.map((group) => (
                <div className="binsr-print-group" key={group.label}>
                  <strong>{group.label}</strong>
                  {group.items.map((item, index) => <p key={`${group.label}-${index}`}>{item}</p>)}
                </div>
              ))}
            </div>
          ) : (
            <p className="binsr-print-empty">No selected findings yet.</p>
          )}
        </section>
      </article>
    </div>
  );
}

function GeneratedDocumentCard({
  document,
  copied,
  onCopy,
  onPrint,
}: {
  document: GeneratedDocument;
  copied: boolean;
  onCopy: () => void;
  onPrint: () => void;
}) {
  return (
    <article className="generated-document-card">
      <div className="generated-document-main">
        <span>BINSR wording copy · {formatGeneratedDocumentTime(document.createdAt)}</span>
        <strong>{document.title}</strong>
        <em>{document.issueNumbers.length} items disapproved · private notes excluded</em>
      </div>
      <div className="generated-document-actions">
        <button className="secondary-action compact-action" type="button" onClick={onCopy}>
          <Copy size={14} />
          {copied ? 'Copied' : 'Copy wording'}
        </button>
        <button className="secondary-action compact-action" type="button" onClick={onPrint}>
          <FileText size={14} />
          Print or save preview PDF
        </button>
      </div>
    </article>
  );
}

function NegotiationCandidateGroup({
  title,
  description,
  tone,
  count,
  collapsible = false,
  defaultOpen = true,
  maxVisible,
  onToggleOpen,
  children,
}: {
  title: string;
  description: string;
  tone: string;
  count: number;
  collapsible?: boolean;
  defaultOpen?: boolean;
  maxVisible?: number;
  onToggleOpen?: (isOpen: boolean) => void;
  children: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [showAll, setShowAll] = useState(false);
  const childItems = Children.toArray(children);
  const hasOverflow = Boolean(maxVisible && childItems.length > maxVisible);
  const visibleChildren = maxVisible && !showAll ? childItems.slice(0, maxVisible) : childItems;
  if (!count) return null;
  return (
    <section className={`candidate-group candidate-group-${tone} ${collapsible ? 'is-collapsible' : ''} ${isOpen ? 'is-open' : 'is-collapsed'}`}>
      <div className="candidate-group-head">
        {collapsible ? (
          <button
            className="candidate-group-toggle"
            type="button"
            onClick={() => setIsOpen((current) => {
              const next = !current;
              onToggleOpen?.(next);
              return next;
            })}
            aria-expanded={isOpen}
            aria-label={`${isOpen ? 'Hide' : 'Show'} ${title} findings`}
          >
            <Clock3 size={14} aria-hidden="true" />
            <span>
              <h3>{title}</h3>
              <p>{description}</p>
            </span>
            <em>{isOpen ? 'Hide' : `Show ${count}`}</em>
            <ChevronDown className={isOpen ? 'open' : ''} size={15} aria-hidden="true" />
          </button>
        ) : (
          <div className="candidate-group-title">
            <Clock3 size={14} aria-hidden="true" />
            <span>
              <h3>{title}</h3>
              <p>{description}</p>
            </span>
          </div>
        )}
      </div>
      {isOpen && <div className="candidate-row-list">{visibleChildren}</div>}
      {isOpen && hasOverflow && (
        <button className="candidate-group-show-more" type="button" onClick={() => setShowAll((current) => !current)}>
          {showAll ? 'Show fewer findings' : `Show ${childItems.length - maxVisible!} more findings`}
        </button>
      )}
    </section>
  );
}

function NegotiationCandidateRow({
  issue,
  selected,
  onToggle,
}: {
  issue: InspectionIssue;
  selected: boolean;
  onToggle: () => void;
}) {
  return (
    <div className={`negotiation-candidate-row ${selected ? 'is-included' : ''}`}>
      <div className="candidate-row-main">
        <strong>{issue.title}</strong>
        <span>{formatSystemLabel(issue.systemCategory || 'miscellaneousSystems')} · {issue.location || issue.nspireLocation || 'Location not specified'}</span>
      </div>
      <div className="candidate-row-side">
        <span>{formatCostRange(issueCostMin(issue), issueCostMax(issue))}</span>
        <button className={selected ? 'is-included' : ''} type="button" onClick={onToggle} aria-pressed={selected}>
          {selected ? <><CheckCircle2 size={14} aria-hidden="true" /> Included</> : 'Include'}
        </button>
      </div>
    </div>
  );
}

function ClientPrepPage({
  payload,
  workspace,
  setWorkspace,
  buyerShare,
  setBuyerShare,
  apiBase,
  routeToken,
  updatedAt,
  setUpdatedAt,
}: {
  payload: InspectionReportPayload;
  workspace: WorkspaceState;
  setWorkspace: React.Dispatch<React.SetStateAction<WorkspaceState>>;
  buyerShare: BuyerShareState;
  setBuyerShare: React.Dispatch<React.SetStateAction<BuyerShareState>>;
  apiBase?: string;
  routeToken?: string;
  updatedAt: string | null;
  setUpdatedAt: React.Dispatch<React.SetStateAction<string | null>>;
}) {
  const [openSections, setOpenSections] = useState<Record<'visibility' | 'message' | 'guide', boolean>>({
    visibility: true,
    message: true,
    guide: false,
  });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [linkState, setLinkState] = useState<'idle' | 'creating' | 'error'>('idle');
  const [notifyState, setNotifyState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [expandedPoint, setExpandedPoint] = useState(0);
  const clientPrepExperienceAvailable = false;
  const visibility = workspace.client_prep.visibility;
  const visibilityControls: Array<{ key: keyof BuyerVisibility; label: string; detail: string; icon: LucideIcon }> = [
    { key: 'issuesList', label: 'Priority findings', detail: 'Show the selected request package.', icon: ListChecks },
    { key: 'severity', label: 'Severity labels', detail: 'Show critical, moderate, and minor labels.', icon: AlertTriangle },
    { key: 'rationale', label: 'Why it matters', detail: 'Show consequence and recommendation detail.', icon: FileText },
    { key: 'costEstimates', label: 'Cost ranges', detail: 'Show extracted repair ranges to the buyer.', icon: DollarSign },
    { key: 'specialists', label: 'Specialist recommendations', detail: 'Show quote or licensed-review flags.', icon: Wrench },
    { key: 'photos', label: 'Photo references', detail: 'Allow photo evidence when available.', icon: Eye },
  ];
  const selectedIssueNumbers = workspace.negotiation_builder.selectedIssueNumbers;
  const recommendedIssueNumbers = getRecommendedNegotiationIssueNumbers(payload);
  const visibleIssueNumbers = selectedIssueNumbers.length
    ? selectedIssueNumbers
    : recommendedIssueNumbers.slice(0, 4);
  const visibleIssueNumberSet = new Set(visibleIssueNumbers);
  const visibleIssues = getIssuesSortedByPriority(payload.issueList.filter((issue) => visibleIssueNumberSet.has(issue.issueNumber)));
  const buyerPayload = shapeBuyerPayload(payload, visibleIssueNumbers, visibility);
  const enabledVisibilityCount = visibilityControls.filter(({ key }) => visibility[key]).length;
  const severityCounts = visibleIssues.reduce<Record<'critical' | 'moderate' | 'minor', number>>((counts, issue) => {
    counts[normalizeSeverity(issue)] += 1;
    return counts;
  }, { critical: 0, moderate: 0, minor: 0 });
  const selectedCosts = costTotalsForIssues(visibleIssues);
  const selectedCostLabel = visibleIssues.length ? formatCompactCostRange(selectedCosts.min, selectedCosts.max) : '$0';
  const fallbackMode = !selectedIssueNumbers.length && visibleIssueNumbers.length > 0;
  const openingStatement = workspace.client_prep.openingStatement || getDefaultOpeningStatement(payload, visibleIssues);
  const closingScript = workspace.client_prep.closingScript || getDefaultClosingScript(visibleIssues);
  const talkingPoints = workspace.client_prep.talkingPoints.length
    ? workspace.client_prep.talkingPoints
    : getDefaultTalkingPoints(visibleIssues);
  const shareUrl = buyerShare.status === 'active' ? buyerShare.buyerUrl : null;
  const viewedLabel = buyerShare.viewedAt ? 'Viewed' : 'Not viewed yet';
  const messageLength = workspace.client_prep.customBuyerMessage.length;
  const notifyRequestedAt = workspace.client_prep.notifyRequestedAt;

  useEffect(() => {
    captureAnalyticsEvent('client_prep_viewed', {
      availability: clientPrepExperienceAvailable ? 'available' : 'coming_soon',
    });
  }, []);

  const setClientPrep = (patch: Partial<WorkspaceState['client_prep']>) => {
    setSaveState('idle');
    setWorkspace((current) => ({
      ...current,
      client_prep: {
        ...current.client_prep,
        ...patch,
      },
    }));
  };

  const setPreset = (preset: 'minimal' | 'balanced' | 'full') => {
    const presetVisibility = {
      ...(preset === 'minimal' ? minimalVisibility : preset === 'full' ? fullVisibility : balancedVisibility),
      agentNotes: false,
    };
    setWorkspace((current) => ({
      ...current,
      client_prep: {
        ...current.client_prep,
        visibilityPreset: preset,
        visibility: presetVisibility,
      },
    }));
  };

  const toggleVisibility = (key: keyof BuyerVisibility) => {
    setWorkspace((current) => ({
      ...current,
      client_prep: {
        ...current.client_prep,
        visibilityPreset: 'custom',
        visibility: {
          ...current.client_prep.visibility,
          [key]: !current.client_prep.visibility[key],
        },
      },
    }));
  };

  const setTalkingPoint = (index: number, value: string) => {
    const next = [...talkingPoints];
    next[index] = value;
    setClientPrep({ talkingPoints: next });
  };

  const addTalkingPoint = () => {
    setClientPrep({ talkingPoints: [...talkingPoints, 'Add the buyer-safe point for this conversation.'] });
    setExpandedPoint(talkingPoints.length);
  };

  const removeTalkingPoint = (index: number) => {
    const next = talkingPoints.filter((_, pointIndex) => pointIndex !== index);
    setClientPrep({ talkingPoints: next });
    setExpandedPoint(Math.max(0, index - 1));
  };

  const hydrateDefaultPrep = () => {
    setClientPrep({
      customBuyerMessage: workspace.client_prep.customBuyerMessage || getDefaultClientMessage(payload, visibleIssues),
      openingStatement,
      closingScript,
      talkingPoints,
    });
    setOpenSections({ visibility: true, message: true, guide: true });
  };

  const savePrepState = async (): Promise<boolean> => {
    if (!apiBase || !routeToken) {
      setSaveState('saved');
      setTimeout(() => setSaveState('idle'), 1600);
      return true;
    }

    setSaveState('saving');
    const clientPrepForSave = {
      ...workspace.client_prep,
      visibility: {
        ...workspace.client_prep.visibility,
        agentNotes: false,
      },
    };
    const updates: StateUpdate[] = [
      { path: 'workspace_state.client_prep', value: clientPrepForSave },
      { path: 'buyer_share_state.visibleIssueNumbers', value: visibleIssueNumbers },
    ];

    try {
      const response = await fetch(`${apiBase.replace(/\/$/, '')}/update-report-state`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: routeToken,
          updates,
          expectedUpdatedAt: updatedAt || undefined,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.error || 'state_update_failed');
      if (data.updated_at || data.updatedAt) setUpdatedAt(data.updated_at || data.updatedAt);
      if (data.buyer_share_state) setBuyerShare((current) => ({ ...current, ...data.buyer_share_state }));
      setSaveState('saved');
      setTimeout(() => setSaveState('idle'), 1600);
      return true;
    } catch {
      setSaveState('error');
      return false;
    }
  };

  const recordClientPrepInterest = async () => {
    if (notifyRequestedAt || notifyState === 'saving') return;
    const requestedAt = new Date().toISOString();

    if (!apiBase || !routeToken) {
      setWorkspace((current) => ({
        ...current,
        client_prep: {
          ...current.client_prep,
          notifyRequestedAt: requestedAt,
        },
      }));
      setNotifyState('saved');
      return;
    }

    setNotifyState('saving');
    try {
      const response = await fetch(`${apiBase.replace(/\/$/, '')}/update-report-state`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: routeToken,
          updates: [{
            path: 'workspace_state.client_prep.notifyRequestedAt',
            value: requestedAt,
          }],
          expectedUpdatedAt: updatedAt || undefined,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.error || 'state_update_failed');
      setWorkspace((current) => ({
        ...current,
        client_prep: {
          ...current.client_prep,
          notifyRequestedAt: requestedAt,
        },
      }));
      if (data.updated_at || data.updatedAt) setUpdatedAt(data.updated_at || data.updatedAt);
      setNotifyState('saved');
    } catch {
      setNotifyState('error');
    }
  };

  const createBuyerLink = async () => {
    if (!visibleIssueNumbers.length) return;
    setLinkState('creating');
    const saved = await savePrepState();
    if (!saved) {
      setLinkState('error');
      return;
    }

    if (!apiBase || !routeToken) {
      const localUrl = `${window.location.origin}/share/${routeToken || 'sample'}`;
      setBuyerShare((current) => ({
        ...current,
        status: 'active',
        buyerUrl: localUrl,
        viewedAt: null,
        revokedAt: null,
        visibleIssueNumbers,
        lastGeneratedPayloadVersion: (current.lastGeneratedPayloadVersion || 0) + 1,
      }));
      setLinkState('idle');
      return;
    }

    try {
      const response = await fetch(`${apiBase.replace(/\/$/, '')}/create-buyer-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: routeToken, visibleIssueNumbers }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.buyerUrl) throw new Error(data?.error || 'buyer_link_failed');
      setBuyerShare((current) => ({
        ...current,
        status: 'active',
        buyerUrl: data.buyerUrl,
        viewedAt: null,
        revokedAt: null,
        visibleIssueNumbers,
        lastGeneratedPayloadVersion: (current.lastGeneratedPayloadVersion || 0) + 1,
      }));
      setLinkState('idle');
    } catch {
      setLinkState('error');
    }
  };

  const copyBuyerLink = async () => {
    if (!shareUrl) return;
    await navigator.clipboard?.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2200);
  };

  const progress = [
    { label: 'Package', complete: visibleIssueNumbers.length > 0 },
    { label: 'Visibility', complete: enabledVisibilityCount > 0 },
    { label: 'Guide', complete: Boolean(openingStatement && closingScript && talkingPoints.length) },
    { label: 'Share', complete: buyerShare.status === 'active' },
  ];

  if (!clientPrepExperienceAvailable) {
    return (
      <div className="page-stack client-prep-page client-prep-coming-soon-page">
        <PageHeader title="Client Prep" subtitle="Turn the inspection into a confident buyer conversation." />

        <Card className="client-prep-coming-soon-hero">
          <div className="client-prep-coming-soon-intro">
            <span className="client-prep-coming-soon-status">Coming Soon</span>
            <h2>Walk into the buyer meeting prepared.</h2>
            <p>Client Prep will turn the BINSR priorities you choose into a clear guide for the buyer conversation.</p>

            <section
              className={`client-prep-launch-panel ${notifyState === 'error' ? 'is-error' : ''}`}
              aria-label="Client Prep interest"
              aria-live="polite"
            >
              <div className="client-prep-launch-copy">
                <div className="client-prep-launch-icon"><MessageSquareText size={18} /></div>
                <div>
                  <strong>{notifyRequestedAt ? 'Interest recorded for this report' : 'Interested in Client Prep?'}</strong>
                  <p>
                    {notifyState === 'error'
                      ? 'Could not save your request. Please try again.'
                      : notifyRequestedAt
                        ? 'Your request is saved so our team can track demand.'
                        : 'Record your interest so our team can track demand while this feature is in development.'}
                  </p>
                </div>
              </div>
              {notifyRequestedAt ? (
                <span className="client-prep-launch-confirmation" role="status">
                  <CheckCircle2 size={16} />
                  Interest recorded
                </span>
              ) : (
                <button
                  className="client-prep-launch-notify"
                  type="button"
                  onClick={recordClientPrepInterest}
                  disabled={notifyState === 'saving'}
                >
                  {notifyState === 'saving' ? <Loader2 className="spin-icon" size={16} /> : <MessageSquareText size={16} />}
                  {notifyState === 'saving' ? 'Recording your interest' : 'Record my interest'}
                </button>
              )}
            </section>
          </div>

          <section className="client-prep-brief-preview" aria-label="Client Prep buyer conversation guide preview">
            <header className="client-prep-brief-preview-head">
              <div className="client-prep-brief-mark"><MessageSquareText size={20} /></div>
              <div>
                <span>Coming to Client Prep</span>
                <strong>Buyer conversation guide</strong>
              </div>
            </header>

            <div className="client-prep-brief-preview-intro">
              <span>For the buyer meeting</span>
              <strong>Bring the inspection into a conversation the buyer can act on.</strong>
            </div>

            <div className="client-prep-brief-preview-list">
              <article>
                <div className="client-prep-brief-step-icon client-prep-brief-step-icon--priority"><AlertTriangle size={18} /></div>
                <div>
                  <strong>What needs attention</strong>
                  <p>Focus the conversation on the priorities that matter now.</p>
                </div>
              </article>
              <article>
                <div className="client-prep-brief-step-icon client-prep-brief-step-icon--conversation"><MessageSquareText size={18} /></div>
                <div>
                  <strong>What to discuss</strong>
                  <p>Give the agent and buyer clear language for the decision.</p>
                </div>
              </article>
              <article>
                <div className="client-prep-brief-step-icon client-prep-brief-step-icon--next"><ListChecks size={18} /></div>
                <div>
                  <strong>What to decide next</strong>
                  <p>Leave the meeting aligned on the practical next steps.</p>
                </div>
              </article>
            </div>
          </section>
        </Card>
      </div>
    );
  }

  return (
    <div className="page-stack client-prep-page ph-no-capture">
      <PageHeader title="Client Prep" subtitle="Prepare the buyer conversation, review the buyer share preview, and publish the share link." />

      <Card className="client-prep-hero-card">
        <div className="client-prep-hero-main">
          <div>
            <span>Buyer conversation readiness</span>
            <strong>{visibleIssues.length ? `${visibleIssues.length} items to explain` : 'No items selected yet'}</strong>
            <p>{fallbackMode ? 'Using recommended request candidates until the agent builds a BINSR draft.' : 'Using the selected BINSR draft for this buyer summary.'}</p>
          </div>
          <div className="client-prep-metrics" aria-label="Client prep metrics">
            <div>
              <span>Visible fields</span>
              <strong>{enabledVisibilityCount} of {visibilityControls.length}</strong>
            </div>
            <div>
              <span>Est. exposure</span>
              <strong>{visibility.costEstimates ? selectedCostLabel : 'Hidden'}</strong>
            </div>
            <div>
              <span>Share status</span>
              <strong>{buyerShare.status === 'active' ? viewedLabel : 'Draft'}</strong>
            </div>
          </div>
        </div>
        <div className="client-prep-progress" aria-label="Client prep progress">
          {progress.map((step, index) => (
            <span key={step.label} className={step.complete ? 'complete' : ''}>
              <em>{step.complete ? <CheckCircle2 size={14} /> : index + 1}</em>
              {step.label}
            </span>
          ))}
        </div>
      </Card>

      {buyerShare.status === 'active' && shareUrl && (
        <Card className="buyer-link-status-card">
          <div className="buyer-link-status-main">
            <span className="status-icon success"><CheckCircle2 size={18} /></span>
            <div>
              <strong>Buyer link active</strong>
              <span>{viewedLabel}{buyerShare.expiresAt ? ` - Expires ${new Date(buyerShare.expiresAt).toLocaleDateString()}` : ''}</span>
              <em>{shareUrl}</em>
            </div>
          </div>
          <div className="buyer-link-actions">
            <button className="secondary-action compact-action" type="button" onClick={copyBuyerLink}>
              <Copy size={15} />
              {copied ? 'Copied' : 'Copy link'}
            </button>
            <button className="secondary-action compact-action" type="button" onClick={() => setPreviewOpen(true)}>
              <Eye size={15} />
              Preview
            </button>
          </div>
        </Card>
      )}

      <div className="client-prep-workbench">
        <div className="client-prep-steps">
          <PrepStepCard
            step={1}
            title="What Buyers Will See"
            detail={`${enabledVisibilityCount} of ${visibilityControls.length} fields visible`}
            complete={enabledVisibilityCount > 0}
            open={openSections.visibility}
            onToggle={() => setOpenSections((current) => ({ ...current, visibility: !current.visibility }))}
          >
            <div className="client-prep-package-note">
              <div>
                <strong>{fallbackMode ? 'Recommended fallback draft' : 'BINSR draft selected'}</strong>
                <span>{visibleIssues.length} issues - {visibility.costEstimates ? selectedCostLabel : 'costs hidden from buyer'}</span>
              </div>
              <span>{severityCounts.critical} critical - {severityCounts.moderate} moderate - {severityCounts.minor} minor</span>
            </div>
            <div className="segmented client-prep-presets" aria-label="Buyer visibility preset">
              {(['minimal', 'balanced', 'full'] as const).map((preset) => (
                <button key={preset} className={workspace.client_prep.visibilityPreset === preset ? 'active' : ''} type="button" onClick={() => setPreset(preset)}>
                  {preset}
                </button>
              ))}
            </div>
            <div className="client-prep-toggle-grid">
              {visibilityControls.map(({ key, label, detail, icon: Icon }) => (
                <label key={key} className="client-prep-toggle">
                  <span className="toggle-copy">
                    <Icon size={16} />
                    <span>
                      <strong>{label}</strong>
                      <em>{detail}</em>
                    </span>
                  </span>
                  <input type="checkbox" checked={visibility[key]} onChange={() => toggleVisibility(key)} />
                </label>
              ))}
            </div>
          </PrepStepCard>

          <PrepStepCard
            step={2}
            title="Personal Message"
            detail={messageLength ? `${messageLength}/500 characters` : 'Optional buyer note'}
            complete={messageLength > 0}
            open={openSections.message}
            onToggle={() => setOpenSections((current) => ({ ...current, message: !current.message }))}
          >
            <label className="client-prep-field">
              <span>Message shown at the top of the buyer view</span>
              <textarea
                maxLength={500}
                rows={5}
                value={workspace.client_prep.customBuyerMessage}
                placeholder="Add a calm note for the buyer before they review the findings."
                onChange={(event) => setClientPrep({ customBuyerMessage: event.currentTarget.value })}
              />
            </label>
            <div className="client-prep-field-actions">
              <span>{messageLength}/500</span>
              <button className="text-link" type="button" onClick={() => setClientPrep({ customBuyerMessage: getDefaultClientMessage(payload, visibleIssues) })}>
                Generate suggestion
              </button>
            </div>
          </PrepStepCard>

          <PrepStepCard
            step={3}
            title="Conversation Guide"
            detail={`${talkingPoints.length} talking points`}
            complete={Boolean(openingStatement && closingScript && talkingPoints.length)}
            open={openSections.guide}
            onToggle={() => setOpenSections((current) => ({ ...current, guide: !current.guide }))}
          >
            <div className="script-field-grid">
              <label className="client-prep-field">
                <span>Opening statement</span>
                <textarea
                  rows={3}
                  value={openingStatement}
                  onChange={(event) => setClientPrep({ openingStatement: event.currentTarget.value })}
                />
              </label>
              <label className="client-prep-field">
                <span>Closing and next step</span>
                <textarea
                  rows={3}
                  value={closingScript}
                  onChange={(event) => setClientPrep({ closingScript: event.currentTarget.value })}
                />
              </label>
            </div>
            <div className="talking-point-editor-list">
              {talkingPoints.map((point, index) => (
                <article key={`${index}-${point.slice(0, 12)}`} className="talking-point-editor">
                  <button type="button" onClick={() => setExpandedPoint(expandedPoint === index ? -1 : index)}>
                    <span>{index + 1}</span>
                    <strong>{point.split('.')[0] || `Talking point ${index + 1}`}</strong>
                    <ChevronDown className={expandedPoint === index ? 'open' : ''} size={16} />
                  </button>
                  {expandedPoint === index && (
                    <div>
                      <textarea rows={3} value={point} onChange={(event) => setTalkingPoint(index, event.currentTarget.value)} />
                      <div className="talking-point-foot">
                        <span>Keep this buyer-safe and contract-aware.</span>
                        <button className="text-link" type="button" onClick={() => removeTalkingPoint(index)}>Remove</button>
                      </div>
                    </div>
                  )}
                </article>
              ))}
            </div>
            <button className="secondary-action compact-action" type="button" onClick={addTalkingPoint}>Add talking point</button>
          </PrepStepCard>

          <Card className="client-prep-action-card">
            <div>
              <strong>Ready to share?</strong>
              <p>Save the prep settings, then generate a buyer link using the selected issues and visibility choices shown in the preview.</p>
              {saveState === 'error' && <span className="client-prep-error">Could not save prep state. Try again before generating the link.</span>}
              {linkState === 'error' && <span className="client-prep-error">Could not generate the buyer link. Check the report token or function response.</span>}
            </div>
            <div className="client-prep-action-row">
              <button className="secondary-action compact-action" type="button" onClick={hydrateDefaultPrep}>
                <RefreshCw size={15} />
                Fill defaults
              </button>
              <button className="secondary-action compact-action" type="button" onClick={savePrepState} disabled={saveState === 'saving'}>
                {saveState === 'saving' ? <Loader2 className="spin-icon" size={15} /> : <CheckCircle2 size={15} />}
                {saveState === 'saved' ? 'Saved' : 'Save prep'}
              </button>
              <button className="primary-button inline-action" type="button" onClick={createBuyerLink} disabled={!visibleIssueNumbers.length || linkState === 'creating'}>
                {linkState === 'creating' ? <Loader2 className="spin-icon" size={16} /> : <Share2 size={16} />}
                {buyerShare.status === 'active' ? 'Regenerate link' : 'Generate buyer link'}
              </button>
            </div>
          </Card>
        </div>

        <aside className="client-prep-preview-rail">
          <Card className="buyer-preview-shell">
            <div className="buyer-preview-head">
              <div>
                <h2>Buyer View</h2>
                <p>Draft share preview based on the current visibility controls.</p>
              </div>
              <button className="text-link" type="button" onClick={() => setPreviewOpen(true)}>
                <Eye size={14} />
                Expand
              </button>
            </div>
            <BuyerFacingPreview
              payload={buyerPayload}
              message={workspace.client_prep.customBuyerMessage}
              visibility={visibility}
              compact
            />
          </Card>
          <Card className="buyer-learning-card">
            <h3>Buyer will learn</h3>
            <div>
              <span>Priority issues</span>
              <strong>{buyerPayload.issueList.length}</strong>
            </div>
            <div>
              <span>Repair ranges</span>
              <strong>{visibility.costEstimates ? 'Shown' : 'Hidden'}</strong>
            </div>
            <div>
              <span>Specialist follow-up</span>
              <strong>{visibility.specialists ? 'Shown' : 'Hidden'}</strong>
            </div>
          </Card>
          <Card className="client-prep-guardrail">
            <ShieldCheck size={18} />
            <p>Buyer copy should frame findings as inspection observations and negotiation options, subject to contract terms and licensed specialist review where needed.</p>
          </Card>
        </aside>
      </div>

      <div className="client-prep-mobile-cta">
        <button className="primary-button inline-action" type="button" onClick={createBuyerLink} disabled={!visibleIssueNumbers.length || linkState === 'creating'}>
          {linkState === 'creating' ? <Loader2 className="spin-icon" size={16} /> : <Share2 size={16} />}
          {buyerShare.status === 'active' ? 'Regenerate link' : 'Generate buyer link'}
        </button>
      </div>

      {previewOpen && (
        <div className="preview-modal" role="dialog" aria-modal="true" aria-label="Buyer view preview">
          <div className="preview-modal-backdrop" onClick={() => setPreviewOpen(false)} />
          <div className="preview-modal-panel">
            <div className="preview-modal-head">
              <div>
                <strong>Buyer View Preview</strong>
                <span>{payload.summary.address || 'Inspection summary'}</span>
              </div>
              <button className="icon-button" type="button" onClick={() => setPreviewOpen(false)} aria-label="Close preview">
                <X size={20} />
              </button>
            </div>
            <BuyerFacingPreview
              payload={buyerPayload}
              message={workspace.client_prep.customBuyerMessage}
              visibility={visibility}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function PrepStepCard({
  step,
  title,
  detail,
  complete,
  open,
  onToggle,
  children,
}: {
  step: number;
  title: string;
  detail: string;
  complete: boolean;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <Card className={`prep-step-card ${complete ? 'complete' : ''}`}>
      <button className="prep-step-toggle" type="button" onClick={onToggle}>
        <span className="prep-step-number">{complete ? <CheckCircle2 size={16} /> : step}</span>
        <div>
          <strong>{title}</strong>
          <span>{detail}</span>
        </div>
        <ChevronDown className={open ? 'open' : ''} size={18} />
      </button>
      {open && <div className="prep-step-body">{children}</div>}
    </Card>
  );
}

const getDefaultClientMessage = (payload: InspectionReportPayload, issues: InspectionIssue[]): string => {
  const criticalCount = issues.filter((issue) => normalizeSeverity(issue) === 'critical').length;
  const specialistCount = issues.filter(issueNeedsSpecialistReview).length;
  const address = payload.summary.address ? ` for ${payload.summary.address}` : '';
  const priorityLine = criticalCount
    ? `${criticalCount} higher-priority ${criticalCount === 1 ? 'item needs' : 'items need'} a clear decision before we finalize the request.`
    : `${issues.length || payload.issueList.length} inspection ${issues.length === 1 ? 'item is' : 'items are'} organized by what we should discuss first.`;
  const specialistLine = specialistCount
    ? `I also flagged ${specialistCount} ${specialistCount === 1 ? 'item' : 'items'} where a licensed specialist or quote may help.`
    : 'I separated seller-request items from normal ownership planning so the discussion stays focused.';

  return [
    `I reviewed the inspection summary${address}.`,
    priorityLine,
    specialistLine,
    'Let us use this to align on what to request, what to verify, and what can wait until after closing.',
  ].join('\n\n');
};

const getDefaultOpeningStatement = (payload: InspectionReportPayload, issues: InspectionIssue[]): string => {
  const condition = payload.summary.overallCondition || 'The report needs buyer and agent review.';
  const criticalCount = issues.filter((issue) => normalizeSeverity(issue) === 'critical').length;
  const riskLine = criticalCount
    ? `${criticalCount} item${criticalCount === 1 ? '' : 's'} should be handled carefully before the inspection window closes.`
    : 'The goal is to focus on function, safety, cost exposure, and seller-request leverage without turning the report into a renovation list.';
  return `${condition}. ${riskLine}`;
};

const getDefaultClosingScript = (issues: InspectionIssue[]): string => {
  if (!issues.length) return 'Next step: select the report-supported items that belong in the BINSR draft, then review them with the buyer before sharing.';
  return `Next step: confirm these ${issues.length} item${issues.length === 1 ? '' : 's'} with the buyer, then use the BINSR draft as the starting point. Any specialist input should be verified before final wording.`;
};

const getDefaultTalkingPoints = (issues: InspectionIssue[]): string[] => {
  const seeded = getIssuesSortedByPriority(issues).slice(0, 4).map((issue) => {
    const action = getIssueActionLabel(issue);
    const specialist = issueNeedsSpecialistReview(issue)
      ? ` A ${issue.specialist_type || 'specialist'} quote or opinion may be useful before relying on the estimate.`
      : '';
    return `${issue.title}: ${action}. ${getIssueImpactText(issue)}${specialist}`;
  });
  return seeded.length ? seeded : ['Start with what affects safety, function, cost exposure, or the buyer decision before closing.'];
};

function BuyerFacingPreview({
  payload,
  message,
  visibility,
  compact = false,
}: {
  payload: InspectionReportPayload;
  message?: string;
  visibility: BuyerVisibility;
  compact?: boolean;
}) {
  const issues = payload.issueList;
  const counts = issues.reduce<Record<'critical' | 'moderate' | 'minor', number>>((total, issue) => {
    total[normalizeSeverity(issue)] += 1;
    return total;
  }, { critical: 0, moderate: 0, minor: 0 });
  const costs = costTotalsForIssues(issues);
  const hasCosts = visibility.costEstimates && costs.max > 0;
  const summaryMessage = message || payload.summary.propertyOverview || 'Review these inspection priorities with your agent before making repair or credit decisions.';
  const headline = payload.summary.overallCondition || 'Inspection priorities';

  return (
    <div className={`buyer-facing-preview ${compact ? 'compact' : ''}`}>
      <div className="buyer-facing-brand">
        <div className="brand-lockup">
          <div className="brand-mark">
            <Home size={15} />
          </div>
          <span>Clear Casa</span>
        </div>
        <span>{payload.summary.address || 'Inspection summary'}</span>
      </div>

      <section className="buyer-facing-message">
        <h2>{headline}</h2>
        <p>{summaryMessage}</p>
      </section>

      <section className="buyer-bottom-line">
        <div className="buyer-bottom-copy">
          <span className="status-icon success"><CheckCircle2 size={17} /></span>
          <div>
            <strong>Bottom line</strong>
            <p>{issues.length ? `${issues.length} selected ${issues.length === 1 ? 'finding' : 'findings'} for the buyer conversation.` : 'No issues are currently visible in this buyer view.'}</p>
          </div>
        </div>
        <div className="buyer-bottom-grid">
          <div>
            <span>Priority items</span>
            <strong>{issues.length}</strong>
          </div>
          {visibility.severity && (
            <div>
              <span>Critical</span>
              <strong>{counts.critical}</strong>
            </div>
          )}
          <div>
            <span>Repair ranges</span>
            <strong>{hasCosts ? formatCompactCostRange(costs.min, costs.max) : 'Hidden'}</strong>
          </div>
        </div>
      </section>

      {visibility.issuesList ? (
        <section className="buyer-facing-issues">
          <h3>What we are reviewing</h3>
          <div className="buyer-facing-issue-list">
            {issues.map((issue) => {
              const severity = normalizeSeverity(issue);
              const Icon = getSystemIcon(issue.systemCategory);
              const costLabel = formatCompactCostRange(issueCostMin(issue), issueCostMax(issue));
              return (
                <article key={issue.issueNumber} className={`buyer-facing-issue issue-${visibility.severity ? severity : 'neutral'}`}>
                  <div className="buyer-facing-issue-head">
                    <span className="system-icon-badge">
                      <Icon size={16} />
                    </span>
                    <div>
                      <strong>{issue.title}</strong>
                      <span>{formatSystemLabel(issue.systemCategory || 'miscellaneousSystems')}{issue.location ? ` - ${issue.location}` : ''}</span>
                    </div>
                    {visibility.severity && <Badge tone={severity}>{severityLabel(severity)}</Badge>}
                  </div>
                  <p>{issue.description || 'Inspection finding selected for buyer review.'}</p>
                  {!compact && visibility.rationale && (
                    <div className="buyer-facing-issue-detail">
                      <span>Why it matters</span>
                      <p>{getIssueImpactText(issue)}</p>
                    </div>
                  )}
                  <div className="buyer-facing-issue-foot">
                    {visibility.costEstimates && issueCostMax(issue) > 0 && <span>{costLabel}</span>}
                    {visibility.specialists && issueNeedsSpecialistReview(issue) && <span>{issue.specialist_type || 'Specialist review'}</span>}
                    {visibility.rationale && <span>{getIssueRecommendationText(issue)}</span>}
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      ) : (
        <section className="buyer-facing-hidden">
          <ListChecks size={17} />
          <p>The itemized issue list is hidden. The buyer will only see the summary message.</p>
        </section>
      )}

      {!compact && (
        <section className="buyer-facing-disclaimer">
          <ShieldCheck size={16} />
          <p>This summary is based on inspection findings and agent preparation. Final requests depend on contract terms, quotes, lender rules, and licensed specialist review where needed.</p>
        </section>
      )}
    </div>
  );
}

function BuyerShareView({ payload, workspace }: { payload: InspectionReportPayload; workspace?: WorkspaceState }) {
  const visibleIssueNumbers = workspace?.negotiation_builder.selectedIssueNumbers || payload.issueList.map((issue) => issue.issueNumber);
  const visibility = workspace?.client_prep.visibility || fullVisibility;
  const buyerPayload = workspace ? shapeBuyerPayload(payload, visibleIssueNumbers, visibility) : payload;
  const message = workspace?.client_prep.customBuyerMessage || buyerPayload.summary.propertyOverview;
  return (
    <main className="buyer-page">
      <div className="buyer-header">
        <div className="brand-lockup">
          <div className="brand-mark">
            <Home size={16} />
          </div>
          <span>Clear Casa</span>
        </div>
        <Badge tone="success">Buyer view</Badge>
      </div>
      <PageHeader title={buyerPayload.summary.address || 'Inspection summary'} subtitle={buyerPayload.summary.overallCondition || 'Shared inspection priorities'} />
      <BuyerFacingPreview payload={buyerPayload} message={message} visibility={visibility} />
    </main>
  );
}

function PageHeader({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="page-header">
      <h1>{title}</h1>
      <p>{subtitle}</p>
    </div>
  );
}

function IconTitle({ icon: Icon, title }: { icon: LucideIcon; title: string }) {
  return (
    <div className="icon-title">
      <span className="icon-tile">
        <Icon size={16} />
      </span>
      <h2>{title}</h2>
    </div>
  );
}

function SeverityPills({
  counts,
  onSelect,
}: {
  counts: Record<'critical' | 'moderate' | 'minor', number>;
  onSelect?: (severity: Exclude<IssueSeverity, 'all'>) => void;
}) {
  return (
    <div className="severity-pills">
      {(['critical', 'moderate', 'minor'] as const).map((severity) => (
        <button
          key={severity}
          className={`severity-pill ${severity}`}
          type="button"
          onClick={() => onSelect?.(severity)}
          aria-label={`View ${severity} issues`}
        >
          <strong>{counts[severity]}</strong> {severityLabel(severity)}
        </button>
      ))}
    </div>
  );
}

function NegotiationStrategyBand({ totals }: { totals: ReturnType<typeof getNegotiationTotals> }) {
  const values = [
    { label: 'Opening ask', value: totals.openingAsk },
    { label: 'Target', value: totals.targetAsk, primary: true },
    { label: 'Minimum comfort', value: totals.walkAwayMinimum },
  ];

  return (
    <div className="strategy-band">
      {values.map((item) => (
        <div key={item.label} className={item.primary ? 'is-primary' : ''}>
          <span>{item.label}</span>
          <strong>{formatCurrency(item.value)}</strong>
        </div>
      ))}
    </div>
  );
}

function TimelineTriageBucket({
  group,
  variant = 'desktop',
}: {
  group: TimelineConcernGroup;
  variant?: 'desktop' | 'mobile';
}) {
  const concern = getTimelinePrimaryConcern(group.systems, group.issues);
  const ConcernIcon = getSystemIcon(concern.id);
  const issueLabel = `${group.issues.length} ${group.issues.length === 1 ? 'issue' : 'issues'}`;
  const concernIssueLabel = `${concern.issueCount} ${concern.issueCount === 1 ? 'finding' : 'findings'}`;
  const bucketCosts = costTotalsForIssues(group.issues);

  return (
    <div className={`${variant === 'mobile' ? 'mobile-timeline-group' : 'timeline-column'} timeline-${group.accentClass}`}>
      <div className="timeline-head">
        <div className="timeline-head-row">
          <span>{group.label}</span>
        </div>
      </div>
      <div className="timeline-issues timeline-triage">
        <article className={`timeline-primary-concern timeline-system-${concern.severity}`}>
          <span className="system-icon-badge">
            <ConcernIcon size={15} />
          </span>
          <div className="timeline-concern-copy">
            <span>Primary concern</span>
            <strong>{concern.label}</strong>
            <em>{concernIssueLabel}</em>
          </div>
        </article>
        <p className="timeline-supporting-detail">
          <span>{issueLabel}</span>
          <span className="timeline-detail-divider" aria-hidden="true" />
          <span className="timeline-estimate">{formatCompactCostRange(bucketCosts.min, bucketCosts.max)} estimated</span>
        </p>
      </div>
    </div>
  );
}

function ReportJobCard({
  icon: Icon,
  tone,
  title,
  job,
  detail,
  metric,
  actionLabel,
  page,
  onNavigate,
}: ReportJobCardProps & { onNavigate: (page: Page) => void }) {
  return (
    <article className={`report-job-card report-job-${tone}`}>
      <div className="report-job-card-head">
        <span className="report-job-icon"><Icon size={17} /></span>
        <div>
          <span>{title}</span>
          <strong>{job}</strong>
        </div>
      </div>
      <p>{detail}</p>
      <div className="report-job-card-foot">
        <em>{metric}</em>
        <button className="report-job-action" type="button" onClick={() => {
          captureAnalyticsEvent('work_report_link_opened', { destination: getAnalyticsPage(page) });
          onNavigate(page);
        }}>
          {actionLabel}
          <ChevronRight size={14} />
        </button>
      </div>
    </article>
  );
}

function SeverityBar({ label, value, total, tone }: { label: string; value: number; total: number; tone: 'critical' | 'moderate' | 'minor' }) {
  return (
    <div className="severity-bar">
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
      </div>
      <div className="bar-track"><span className={`bar-${tone}`} style={{ width: `${total ? (value / total) * 100 : 0}%` }} /></div>
    </div>
  );
}

function IssueList({
  issues,
  selected,
  onToggle,
  compact = false,
}: {
  issues: InspectionIssue[];
  selected?: Set<number>;
  onToggle?: (issueNumber: number) => void;
  compact?: boolean;
}) {
  if (!issues.length) return <p className="empty-copy">No issues in this view.</p>;
  return (
    <div className={compact ? 'compact-issue-list' : 'issue-card-list'}>
      {issues.map((issue) => (
        <IssueCard key={issue.issueNumber} issue={issue} selected={selected?.has(issue.issueNumber)} onToggle={onToggle ? () => onToggle(issue.issueNumber) : undefined} compact={compact} />
      ))}
    </div>
  );
}

function IssueTable({
  issues,
  selected,
  onToggle,
}: {
  issues: InspectionIssue[];
  selected: Set<number>;
  onToggle: (issueNumber: number) => void;
}) {
  return (
    <Card className="issue-table-card">
      <div className="issue-table-scroll">
        <table className="issue-table">
          <colgroup>
            <col className="issue-table-selection-column" />
            <col className="issue-table-issue-column" />
            <col className="issue-table-severity-column" />
            <col className="issue-table-timing-column" />
            <col className="issue-table-cost-column" />
          </colgroup>
          <thead>
            <tr>
              <th scope="col">BINSR draft</th>
              <th scope="col">Issue details</th>
              <th scope="col">Severity</th>
              <th scope="col">Timing</th>
              <th scope="col">Est. cost</th>
            </tr>
          </thead>
          <tbody>
            {issues.map((issue) => {
              const severity = normalizeSeverity(issue);
              const isSelected = selected.has(issue.issueNumber);
              return (
                <tr key={issue.issueNumber}>
                  <td>
                    <button className={isSelected ? 'table-select-button selected' : 'table-select-button'} type="button" onClick={() => onToggle(issue.issueNumber)} aria-pressed={isSelected} title={isSelected ? 'Remove from BINSR draft' : 'Include in BINSR draft'}>
                      {isSelected ? 'Included' : 'Include'}
                    </button>
                  </td>
                  <td className="issue-table-title">
                    <strong title={`#${issue.issueNumber} ${issue.title}`}>#{issue.issueNumber} {issue.title}</strong>
                    <span className="issue-table-meta">
                      <span><b>System</b>{formatSystemLabel(issue.systemCategory || 'miscellaneousSystems')}</span>
                      <span><b>Location</b>{issue.location || issue.nspireLocation || 'Location not listed'}</span>
                    </span>
                  </td>
                  <td><Badge tone={severity}>{severityLabel(severity)}</Badge></td>
                  <td>{issue.recommendedTimeframe || timeframeBuckets[getTimeframeBucket(issue)].label}</td>
                  <td>{formatCostRange(issueCostMin(issue), issueCostMax(issue))}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function IssueCard({
  issue,
  selected,
  onToggle,
  compact = false,
}: {
  issue: InspectionIssue;
  selected?: boolean;
  onToggle?: () => void;
  compact?: boolean;
}) {
  const severity = normalizeSeverity(issue);
  const isSelected = Boolean(selected);
  const timeframeLabel = issue.recommendedTimeframe || timeframeBuckets[getTimeframeBucket(issue)].label;
  const systemLabel = formatSystemLabel(issue.systemCategory || 'miscellaneousSystems');
  const impactText = getIssueImpactText(issue);
  const recommendationText = getIssueRecommendationText(issue);
  const specialistLabel = getIssueSpecialistLabel(issue);
  const typicalLabel = getIssueTypicalLabel(issue);
  return (
    <article className={`issue-card issue-card-${severity} ${compact ? 'compact' : ''}`}>
      <div className="issue-main">
        <div>
          <div className="issue-card-topline">
            <div className="issue-card-heading">
              <div className="issue-kicker">
                <span className="issue-number">#{issue.issueNumber}</span>
                <span>{systemLabel}</span>
                {!compact && <Badge tone={severity}>{severityLabel(severity)}</Badge>}
              </div>
              <h3>{issue.title}</h3>
            </div>
            {!compact && (
              <div className="issue-card-controls">
                {onToggle && (
                  <button className={selected ? 'secondary-button selected' : 'secondary-button'} type="button" onClick={onToggle} aria-pressed={isSelected} title={selected ? 'Remove from BINSR draft' : 'Include in BINSR draft'}>
                    {selected ? <><CheckCircle2 size={15} aria-hidden="true" />Included in BINSR draft</> : 'Include in BINSR draft'}
                  </button>
                )}
              </div>
            )}
          </div>
          {!compact && (
            <>
              <div className="issue-brief-grid">
                <div>
                  <DollarSign size={15} />
                  <span>Est. cost</span>
                  <strong>{formatCostRange(issueCostMin(issue), issueCostMax(issue))}</strong>
                </div>
                <div>
                  <Clock3 size={15} />
                  <span>Timing</span>
                  <strong>{timeframeLabel}</strong>
                </div>
                <div>
                  <MapPin size={15} />
                  <span>Location</span>
                  <strong>{issue.location || issue.nspireLocation || 'Location not listed'}</strong>
                </div>
                <div>
                  <House size={15} />
                  <span>Home context</span>
                  <strong>{typicalLabel}</strong>
                </div>
              </div>

              <details className="issue-details" onToggle={(event) => {
                if (event.currentTarget.open) {
                  captureAnalyticsEvent('issue_detail_opened', {
                    severity,
                    system_category: issue.systemCategory || 'miscellaneousSystems',
                  });
                }
              }}>
                <summary>
                  <span className="issue-details-closed-label">View inspector finding and next step</span>
                  <span className="issue-details-open-label">Hide inspector finding and next step</span>
                  <ChevronDown size={14} aria-hidden="true" />
                </summary>
                <div className="issue-details-panel">
                  <div className="issue-detail-content">
                    <section className="issue-detail-finding">
                      <span>Inspection finding</span>
                      <p>{issue.description || 'No finding description was extracted.'}</p>
                    </section>
                    <section className="issue-detail-next-step">
                      <span>Recommended next step</span>
                      <p>{recommendationText}</p>
                      <div className="issue-detail-action-lines">
                        <div>
                          <strong>Recommended approach</strong>
                          <span>{issue.repair_approach || 'No repair approach was extracted.'}</span>
                        </div>
                        <div>
                          <strong>Who to involve</strong>
                          <span>{specialistLabel}</span>
                        </div>
                      </div>
                    </section>
                    <section className="issue-detail-why">
                      <span>Why it matters</span>
                      <p>{impactText}</p>
                    </section>
                  </div>
                </div>
              </details>
            </>
          )}
          {compact && (
            <div className="issue-tags">
              <span>{systemLabel}</span>
              <span>{issue.location || issue.nspireLocation || 'Location not listed'}</span>
            </div>
          )}
        </div>
      </div>
      {compact && (
        <div className="issue-side">
          <>
            <strong>{formatCostRange(issueCostMin(issue), issueCostMax(issue))}</strong>
            <span>{timeframeLabel}</span>
            {onToggle && (
              <button className={selected ? 'secondary-button selected' : 'secondary-button'} type="button" onClick={onToggle} aria-pressed={isSelected} title={selected ? 'Remove from BINSR draft' : 'Include in BINSR draft'}>
                {selected ? <><CheckCircle2 size={15} aria-hidden="true" />Included in BINSR draft</> : 'Include in BINSR draft'}
              </button>
            )}
          </>
        </div>
      )}
    </article>
  );
}

export default App;
